/**
 * Every learner-facing string that is transcribed from design_frames.pdf,
 * verbatim.
 *
 * The lowercase, the trailing periods, the `---` separators and the odd
 * spacing in "log in/ sign up to begin" are all deliberate voice, not typos
 * — see docs/DESIGN-SYSTEM.md section 1. Nothing here may be "tidied".
 * src/design/copy.test.ts asserts the shapes that are easy to lose in an
 * edit (trailing periods, lowercase nav, the exact placeholder).
 */

import type { OUTCOME_ANSWERS, OutcomeQuestion } from "@pushback-and-chaos/contracts";

export const BRAND = {
  eyebrow: "Real Case Simulations",
  wordmarkLine1: "Pushback &",
  wordmarkLine2: "Chaos",
  taglineLine1: "Stop memorizing robotic frameworks.",
  taglineLine2: "Start building real-world skills.",
  logIn: "log in.",
  bullets: "•••",
  /** Frame 1 renders this lowercase, frames 2-3 uppercase. See ambiguity A1. */
  copyright: "© FCG 2026",
  short: "P&C",
} as const;

export const LANDING_NAV = [
  { id: "start", label: "start." },
  { id: "scenarios", label: "scenarios." },
  { id: "calibration", label: "calibration." },
  { id: "settings", label: "settings." },
  { id: "events", label: "events." },
] as const;

export const TRACKS = [
  { n: 1, key: "CALIBRATION", label: "1. Calibration" },
  { n: 2, key: "FRAMEWORKS", label: "2. Frameworks" },
  { n: 3, key: "CONFLICT_MANAGEMENT", label: "3. Conflict Management" },
  { n: 4, key: "CREATIVITY", label: "4. Creativity" },
  { n: 5, key: "CHAOS", label: "5. Chaos" },
] as const;

/**
 * The tension line on a library card: the stakes of a case in the frames'
 * voice, never the company name, nothing the learner cannot see on the
 * opening desk, no mechanical vocabulary. Six forms are allowed (see
 * docs/case-authoring/03-case-template/README.md, "The tension line"):
 *
 *   numbered       "Sixty-eight clinics. Eight days. One board pre-read."
 *   consensus      the room's certainty, quoted — the claim the learner will test
 *   contradiction  two facts that refuse to fit
 *   deadline       the pressure context in a stakeholder's mouth
 *   question       the case's central question, short
 *   desk           the object or margin note the opening scene turns on
 *
 * RULE: no two cards that sit together under one track heading use the same
 * form (copy.test.ts reads every cases/<slug>/case-bible.json and enforces
 * it). Same track only: the routine's pick is not constrained by the case
 * before it in the planned order — that clause once barred Larkmead's best
 * line and produced a worse one. The authoring routine drafts all six forms per case in its README and
 * picks one; the pick lands here at merge. A case without a line falls back
 * to its engagement question.
 */
export const CASE_TENSION: Readonly<Record<string, string>> = {
  harborlight: "Sixty-eight clinics. Eight days. One board pre-read.",
  "ninebark-engineering": "More engineers than ever. Less for them to do.",
  "ostrander-freight": "Same trucks, same freight, later every month.",
  "kesslow-precision": "Shipping more than ever, and making a fifth less on every dollar.",
  wrenhall: "Everyone agrees the growth stopped. Nobody agrees what growth means.",
  "sedgewell-imaging": "Nine centres, forty miles apart, every one down by the same amount.",
  larkmead: "A quarter of the customers gone, and every number about them better.",
  cawthorne: "The cheap savings are gone. Hugh put that in writing before Monday.",
  halverford: "Costs went up everywhere. Is that where the margin went?",
  "alderwick-health": "In the margin: two of these four had a better year.",
  cranemoor: "Every customer in that room is a letter of the alphabet.",
  millgate: "Twenty-one million more sold this year. Where did it come from?",
  marrowfield: "Who decided what a small shop owes the warehouse?",
  tollemarsh: "It's the engineers leaving. The managing director has already put it in writing.",
  stanwick: "It was the market. The board has already been told so.",
  drayvale: "Procurement answers on the twenty-third. Two other caterers have walked the plants.",
  brindlemere: "In the margin, in ballpoint: an average of what, exactly?",
  ellerby: "Every page starts three years ago. Who chose three?",
  umberton: "Slower by four days. Slower than what, exactly?",
  ryecote: "In the margin, in pencil: same full aircraft, fewer of them.",
  thirlwell: "They publish the board's answer on Tuesday. Beside their own.",
  havershill: "The order count holds steady. What the business keeps shrinks.",
  skellbourne: "Reported punctuality barely changes; complaints about late and missed buses more than double.",
  quarrow: "Membership has grown since last March. Membership revenue has shrunk.",
  redmarley: "Comparable shops lose about two percent of sales; the chain loses more than nine.",
  rivelstone: "Each operative closes more jobs a day, yet total repairs spending has risen.",
  saltmere: "The room agrees: nine hotels had a bad year, and that explains everything.",
  brackmill: "Fifteen pub groups, one falling total: which of them is taking less beer?",
  pellanby: "“The membrane lot is acknowledged. Just tell us how much.”",
};

export type TensionForm = "numbered" | "consensus" | "contradiction" | "deadline" | "question" | "desk";

/** Which of the six forms each line uses — the input to the no-repeat rule. */
export const CASE_TENSION_FORM: Readonly<Record<keyof typeof CASE_TENSION, TensionForm>> = {
  harborlight: "numbered",
  "ninebark-engineering": "contradiction",
  "ostrander-freight": "contradiction",
  "kesslow-precision": "contradiction",
  wrenhall: "consensus",
  "sedgewell-imaging": "contradiction",
  larkmead: "contradiction",
  cawthorne: "consensus",
  halverford: "question",
  "alderwick-health": "desk",
  cranemoor: "desk",
  millgate: "question",
  marrowfield: "question",
  tollemarsh: "consensus",
  stanwick: "consensus",
  drayvale: "deadline",
  brindlemere: "desk",
  ellerby: "question",
  umberton: "question",
  ryecote: "desk",
  thirlwell: "deadline",
  havershill: "contradiction",
  skellbourne: "contradiction",
  quarrow: "contradiction",
  redmarley: "contradiction",
  rivelstone: "contradiction",
  saltmere: "consensus",
  brackmill: "question",
  pellanby: "consensus",
};

/**
 * The case library's own copy (library pass, 2026-09-08). Shelf labels follow
 * the reference frames — title case, no period — because they are headings
 * over a row, not affordances; every affordance keeps the frames' lowercase
 * and trailing period. The card's three rows are labelled for the learner:
 * the bible's track, pressure context and primary dimension read as track,
 * goal and skill.
 */
export const LIBRARY = {
  title: "Case Library.",
  continueBox: "Continue",
  savedBox: "Saved",
  savedEmpty: "Drag a case here to keep it.",
  continueEmpty: "Nothing open. Pick a case from the shelf.",
  calibrationShelf: "Calibration Cases",
  curatedShelf: "Curated for you by AJ",
  /**
   * The locked rows' sentence is composed from what is still open (AJ intake,
   * 2026-09-24; library-shelves.ts lockedMessageFor): the lead, then the
   * conditions not yet met, in this order, and never a numeral.
   */
  lockedLead: "To open the rest of the library:",
  lockedNeeds: {
    firstCase: "finish {case}",
    plan: "read your first development plan",
    signIn: "sign in",
  },
  cardTrack: "track:",
  cardGoal: "goal:",
  cardSkill: "skill:",
  /**
   * The kind row at the top of a case paper (2026-09-22): what the paper is,
   * then the track after a middle dot — "case · calibration" on the shelf,
   * "continue" / "saved" on the rail's keepsakes.
   */
  kindCase: "case",
  kindContinue: "continue",
  kindSaved: "saved",
  open: "open.",
  startOver: "start over.",
  walkBackIn: "walk back in.",
  readPrologue: "read the prologue.",
  readPrologueAgain: "read the prologue again.",
  save: "save.",
  unsave: "unsave.",
  putDown: "put it down.",
  daysToGo: (n: number): string => (n === 1 ? "one day to go." : `${String(n)} days to go.`),
  /** The two bars' captions, shown on hover; the accessible names are the same words. */
  timeBar: (n: number): string => `time left: ${n === 1 ? "one day to go." : `${String(n)} days to go.`}`,
  evidenceBar: "progress: the evidence you have found so far.",
  planRead: "I have read this.",
  planReadDone: "Read.",
  /** The "are you sure" before a run is thrown away. Plain about what is lost; no exclamation. */
  startOverTitle: "Start this case over?",
  startOverBody: "Starting over deletes your progress on this case. Everything you have asked for, found and written in your current run will be gone, and the case begins again from day one.",
  startOverConfirm: "start over.",
  startOverCancel: "keep my run.",
} as const;

/**
 * The front door (home pass, 2026-09-09): the coach speaks first, and the
 * cards it pushes carry a lowercase label with a trailing period, in the
 * frames' voice. The composer's placeholder is the reference frame's own
 * line. Nothing here is a number, and nothing here is a stat.
 */
export const HOME = {
  inputPlaceholder: "Work with AJ and plan your next move.",
  reading: "AJ is reading the record.",
  /** Before any engagement is finished there is no record to read; the coach is writing its first words to this learner, and that takes a moment. */
  readingFirst: "AJ is writing your first opening. There is no record yet, so this takes a moment.",
  gated: "Finish onboarding first. AJ meets you here once there is a record to read.",
  unavailable: "AJ could not be reached just now. The shelf is still yours.",
  replyFailed: "That did not reach AJ. Ask again in a moment.",
  dragHint: "click and drag.",
  coach: "AJ",
  you: "you",
  /**
   * The desk's own chrome, in the play surface's vocabulary (2026-09-20):
   * a label, who put the papers there, and one line at the foot saying what
   * the desk is for. No count — the play desk says "2 documents" and this
   * surface renders no numeral.
   */
  /** The conversation's own name, for the reading area a screen reader lands in. */
  coachPanel: "AJ",
  /** The banner over the conversation (Andre, 2026-09-22), drawn like the desk's: a small tracked label over a hairline. */
  coachLabel: "Chat with AJ",
  /**
   * The page control's end slot, just above the composer: back to the newest
   * page, in the front door's lowercase voice where the story says "Latest".
   */
  pagerLatest: "latest.",
  /**
   * "View the moment" (C05): under a coach line that pointed at the record,
   * one control per moment, in words. The caption says whose words the
   * excerpt is, from the reference itself and never from the prose.
   */
  viewMoment: "view the moment.",
  viewMomentLabel: "Where this comes from",
  evidenceAttribution: {
    LEARNER_WORDS: "your words",
    ASSESSOR_ACCOUNT: "the assessor's account of your move",
    ASSESSOR_REASON: "the assessor's reason",
    DEBRIEF: "the debrief",
  },
  deskLabel: "On your desk",
  deskFoot: "Open one when you are ready.",
  deskEmpty: "Nothing here yet.",
  /** THE BIN (2026-09-23, Andre): bottom right of the desk; a card dragged onto it is gone. */
  bin: "the bin",
  binHint: "Drag a card here to put it away.",
  /**
   * ARRANGING THE DESK (2026-10-31, Andre): the first arrangement, "tidy.",
   * and its one "undo.", in the desk's heading in the doors' lowercase voice.
   * The tidy piles the papers by kind (Andre's second pass the same day); its
   * first form, rows, is kept for the comparison and has its own hint. The
   * other arrangements from the ideation stand behind one word, "arrange
   * by.": by status, in AJ's order, by skill. Each is a command done once,
   * not a mode. Staged behind a flag; provisional words, Codex's pass owed
   * before they reach a learner.
   */
  tidy: "tidy.",
  tidyHint: "Pile the papers by kind, the one to open first on top. They stay free to move.",
  tidyRowsHint: "Lay the papers out in rows so each one shows. They stay free to move.",
  undoArrange: "undo.",
  undoArrangeHint: "Put the papers back where they were before you arranged them.",
  arrangeBy: "arrange by.",
  arrangeByHint: "Other ways to lay the desk out. Each is done once; the papers stay free to move.",
  arrangeStatus: "status.",
  arrangeStatusHint: "Pile the cases by their standing: what you have open, what AJ pushed, what is next, what you saved, what came from the shelf.",
  arrangeHand: "AJ's order.",
  arrangeHandHint: "Lay the hand as AJ dealt it, first to last, with the rest of the desk beside it.",
  arrangeSkill: "skill.",
  arrangeSkillHint: "Pile the papers by the skill they train, the case to open first on top.",
  /**
   * THE CONNECTION between a recommendation and its file (2026-10-31, the
   * push's focus link; desk-links.ts). Provisional words, Codex's pass owed.
   * The marker's word in the conversation and on the file, the focus said
   * beside the recommendation, and the way to an end that is off the screen.
   */
  linkOnDesk: "on your desk.",
  linkOnDeskHint: (title: string): string => `Show ${title} on your desk.`,
  linkWhy: "why this case?",
  linkWhyHint: "Show where AJ explained this.",
  linkForFocus: "for your focus.",
  linkForEarlierFocus: "for an earlier focus.",
  /** "my focus." (2026-10-31): offered only while a focus is chosen and AJ has pushed a case for it. Provisional words. */
  arrangeFocus: "my focus.",
  arrangeFocusHint: "Gather the cases AJ chose for your focus under the focus paper, their titles showing, with the rest of the desk piled beside them.",
  /**
   * The front door is where a new learner meets the product (pedagogy review,
   * step 5; AJ intake, 2026-09-24): the coach introduces itself with its
   * script (contracts: AJ_INTRO), asks what brings them here, talks with them
   * in its own voice, and reads the profile back before it is kept. The
   * composer is shut while the script is being typed, and says so.
   */
  introWait: "AJ is finishing a thought.",
  introPlaceholder: "Tell me a little about yourself and what brings you here.",
  readingYou: "Give me a moment to read that.",
  confirmProfile: "that's right.",
  redoProfile: "start over.",
  /** The composer under a read-back takes a correction, not a fresh introduction (onboarding pass, 2026-09-13). */
  correctPlaceholder: "Tell me what I've missed or got wrong.",
  readBackApproaches: "That's how I'd describe your approach so far. It shapes how hard the cases push, never what you're allowed to try.",
  readBackApproachesProvisional: "From what you've told me, you may tend to work {approaches}. That's an early impression; I have more listening to do.",
  readBackApproachesNone: "I don't have a sense of how you work yet. We can come back to that once there's a case to talk through.",
  /** The four levels as the learner may pick them, no model in the loop. */
  levelPills: {
    STUDENT: "studying.",
    EARLY_CAREER: "first few years of work.",
    EXPERIENCED: "well established.",
    SENIOR: "leading teams or engagements.",
  },
  pickOne: "Pick one above.",
  /**
   * THE REHEARSAL (onboarding pass, idea 9): one move before the first case,
   * with the two ways of saying it shown side by side first (idea 6).
   */
  rehearsalIntro: [
    "One small aside before you open your first case. Let the people in it hear your thinking. They have plenty of opinions, but they can't read your mind.",
    "Write what you think, what you've seen that makes you think it, and what you want to do next. A bare request gives people a task. Sharing the thinking gives them something to answer, question or disagree with.",
  ],
  exhibitTitle: "Two ways to say the same move.",
  exhibit: {
    terseLabel: "asked like this",
    terse: "Send me the clinic numbers.",
    terseResult: "Hugh sips his coffee, waiting. “Oh, was that all? ‘Clinic numbers’ means nothing to me. Find the right person to ask.”",
    narratedLabel: "told like this",
    narrated: "I think the regional totals are hiding something, because the clinic figures in the board pack don't move when the region does, so I want to ask Dana for the clinic-level sheet.",
    narratedResult: "Dana pauses, then says the clinic sheet exists but the regional office asked her not to circulate it. She will send it if you ask the CFO first. Now you have a lead and a person.",
  },
  rehearsalAsk: "Fancy trying a move before you begin? This little scene stands on its own, outside the cases. Your move and my reflection will stay in your first book.",
  rehearsalQuestion: "What do you think is going on, what in the scene makes you think so, and what would you do first?",
  rehearsalPlaceholder: "Say what you think, what you have seen, and what you will do.",
  rehearsalReading: "Reading your move.",
  rehearsalSkip: "skip for now.",
  rehearsalBegin: "begin.",
  rehearsalDone: "Keep those pieces together as you go: what you think, what you've noticed, and what you'll do. The next move is yours.",
  /**
   * The kind row of a pushed card (2026-09-22): the door word, then the
   * track after a middle dot — "continue · conflict management",
   * "next · frameworks". Short, because the coach's reason underneath
   * already explains the recommendation (Codex's review). The plan has no
   * track and stands alone.
   */
  cardKind: {
    NEXT: "next",
    CONTINUE: "continue",
    SAVED: "saved",
    CALIBRATION: "calibration",
    PLAN: "your plan",
    SHELF: "from the shelf",
    PUSHED: "from AJ",
  },
  /** The card's second door: the case's own lightbox, exactly the library's. */
  cardDetails: "details.",
  cardAction: {
    NEXT: "open.",
    CONTINUE: "walk back in.",
    SAVED: "open.",
    CALIBRATION: "open.",
    PLAN: "read it.",
    SHELF: "open.",
    PUSHED: "open.",
  },
} as const;

/** The pressure context, as the card's `goal:` — the reason the client is in the room, in plain words. */
export const GOAL_LABEL: Readonly<Record<string, string>> = {
  REFINANCING: "refinancing",
  SALE_PROCESS: "a sale",
  ACQUIRER_DILIGENCE: "diligence",
  ACTIVIST_INVESTOR: "an activist",
  TURNAROUND_LENDER: "a lender",
  REGULATOR: "a regulator",
  LITIGATION: "litigation",
  PUBLIC_INCIDENT: "an incident",
  FAMILY_SUCCESSION: "family succession",
  NEW_LEADER: "a new leader",
  CUSTOMER_ULTIMATUM: "an ultimatum",
};

/** The primary rubric dimension, as the card's `skill:` — the one sanctioned rendering of a dimension name. */
export const SKILL_LABEL: Readonly<Record<string, string>> = {
  STRUCTURE: "structure",
  QUANTITATIVE: "quantitative",
  JUDGMENT: "judgment",
  COMMUNICATION: "communication",
  AMBIGUITY_TOLERANCE: "ambiguity tolerance",
  CALIBRATION: "calibration",
};

export const AUTH = {
  header: "log in/ sign up to begin",
  // The privacy line is a commitment, and it must never claim more than the
  // architecture delivers. "Inaccessible to anyone" and "100%" were both
  // false in the strong sense (AI providers process plaintext and retain it
  // briefly; the operator can technically reach the database), so the line
  // stated who USES the data and pointed at the policy for the precise flows.
  // The Z.ai switch (the review, 2026-10-29; Codex, round two and its
  // revision): "no one else uses them" cannot stand beside the uses Z.ai's
  // terms allow (law, policy enforcement, abuse prevention, usage data) or the
  // providers' abuse monitoring, and "no AI is ever trained on them" rests,
  // for Z.ai, on its published terms alone, which a one-line card cannot
  // qualify, so the card says what is true and the policy carries the rest.
  // Andre confirmed this line (his ruling 4 of 2026-10-29: Codex's line ships;
  // docs/COACH-ZAI-DIRECT-2026-10-29.md).
  privacyBeforeLink: "Your sessions are for you and your coach. AI providers process them. ",
  privacyLink: "Privacy policy",
  privacyAfterLink: ".",
  google: "Sign in with Google.",
  facebook: "Sign in with Facebook.",
  guest: "Guest Session.",
  guestContinue: "continue as guest.",
  guestFresh: "start a fresh guest.",
  separator: "---",
  emailIntro: "Or sign in with email:",
  emailPlaceholder: "sample@email.com",
  passwordPlaceholder: "password",
  namePlaceholder: "what should we call you?",
  // The frames' words, a live link since 2026-10-22: it opens the forgot-password form below.
  passwordReset: "password reset",
  signIn: "sign in.",
  signUp: "create an account.",
  toggleToSignUp: "no account yet?",
  toggleToSignIn: "already have one?",
  passwordTooShort: "Passwords need to be at least 12 characters.",
  // THE ACCOUNT RELEASE (2026-10-22, Andre: hard verification, password reset, the terms). Every
  // new line below was reviewed in Codex's voice pass, 2026-10-22.
  // Codex voice pass (2026-10-22): the terms notice, one line above every door (guest, Google, email).
  // The link word is the page's title less its period.
  termsBeforeLink: "By continuing, you agree to the ",
  termsLink: "Terms",
  termsAfterLink: ".",
  // Codex voice pass: the card after an email sign-up, and after a sign-in better-auth refuses
  // with 403 EMAIL_NOT_VERIFIED (it has just sent a new link, `sendOnSignIn`, unless one went out a
  // moment ago). A sign-up answers the same whether or not the address has an account (better-auth
  // 1.7.2 under requireEmailVerification), so the sign-up line is conditional, and since the review
  // of 2026-10-22 (apps/api better-auth.ts `onExistingUserSignUp`): a new address is sent a
  // confirmation link; an unconfirmed account with only password sign-in or no way in loses its
  // password and sessions and is sent a set-password link. An unconfirmed account with Google
  // sign-in is left as it is and sent nothing, as is a confirmed account. The short line below
  // names only the password case; it is identical for every sign-up answer.
  checkEmailTitle: "Confirm your email address.",
  checkEmailSignedUp: (email: string): string =>
    `If ${email} is new here, a confirmation link is on its way. If it has an unconfirmed account that uses only a password, a link to set your password is on its way instead. Already confirmed this address? Sign in.`,
  checkEmailUnverified: (email: string): string => `${email} isn't confirmed yet. Look in your email for a confirmation link.`,
  // "A link to confirm": after a sign-up the link sent may instead be one to set a password, which
  // opens the reset page (the review of 2026-10-22).
  checkEmailOpen: "The confirmation link opens a page. Press the button there to confirm your address and sign in.",
  // Ruling 8 (docs/ANDRE-RULINGS-2026-10-12.md:18): the claim runs on the first signed-in request in
  // the browser holding the guest cookie, so a link opened elsewhere leaves the guest work behind. The
  // review of 2026-10-22: it waits only while the guest lasts, 30 days from the guest session's start
  // (DEFAULT_GUEST_RETENTION_DAYS, packages/db repositories/sessions.ts; the privacy page and the terms).
  checkEmailGuest:
    "To keep your guest work, confirm your address in this browser and sign in here before your guest session expires, 30 days after it began. If you use another browser or device, return here and sign in before then. A link to set your password asks you to sign in after saving it.",
  // VERIFICATION_LINK_TTL_SECONDS (packages/mail), better-auth's emailVerification.expiresIn: the
  // refused sign-in's link, which is only ever a confirmation.
  checkEmailLasts: "The link lasts 24 hours.",
  // After a sign-up, either link may be the one sent: VERIFICATION_LINK_TTL_SECONDS, and
  // PASSWORD_RESET_LINK_TTL_SECONDS for the link to set a password (used once).
  checkEmailLastsSignedUp: "A confirmation link lasts 24 hours. A link to set your password works once and lasts one hour.",
  resend: "send another link.",
  // The signed-out resend answers the same whether or not the address waits to be confirmed, and
  // the api holds back a send past its per-account limit without saying so.
  resent: "If this address has an unconfirmed account, another confirmation link is on its way. If it doesn't arrive, wait before asking again: sends are limited to one a minute and five a day.",
  notSent: "We couldn't send that request. Try again in a moment.",
  // Codex voice pass (review, 2026-10-22): better-auth's two refusals of a resend from a
  // browser that is signed in. 400 EMAIL_ALREADY_VERIFIED comes only when the session's own address
  // is the one asked about and it is already confirmed; 400 EMAIL_MISMATCH when the session belongs
  // to a different address, which only logging out (the header's "Log out.") clears.
  alreadyConfirmed: "This address is already confirmed. You can sign in.",
  otherAccount: "This browser is signed in with a different address. Log out first, then ask for a new link.",
  backToSignIn: "back to sign in.",
  // Codex voice pass: the reset answer is the same whether or not an account exists. A link goes
  // to an account with a password or no way in; never to a Google-only account.
  forgotIntro: "Enter your account email address to ask for a password link.",
  forgotSend: "send a reset link.",
  // PASSWORD_RESET_LINK_TTL_SECONDS (packages/mail): one hour, used once.
  forgotSent: "If this address has an account that can use a password link, one is on its way. It works once and lasts one hour. If you only sign in with Google, use Google to sign in. Sends are limited to one a minute and five a day, so wait before asking again.",
  // Codex voice pass: better-auth's refusals, by code (data/auth-errors.ts).
  wrongPassword: "That email address and password don't match an account.",
  // The review of 2026-10-22: the resend and reset doors allow five in fifteen minutes (apps/api
  // better-auth.ts AUTH_RATE_LIMIT_RULES), and a refusal holds until the window has passed.
  tooManyTries: "Too many requests just now. You may need to wait up to about 15 minutes before trying again.",
  signInNotThrough: "We couldn't sign you in. Try again in a moment.",
  // For Codex's voice pass (2026-10-22, the pre-release review):
  // better-auth's z.email() refuses an address the browser's type=email lets through (`ana@gmail`):
  // sign-in's 400 INVALID_EMAIL, and the 400 VALIDATION_ERROR of sign-up, the resend and the reset
  // request (data/auth-errors.ts). Every door says it, since trying again in a moment cannot help.
  addressIncomplete: "That email address looks incomplete. Please correct it and try again.",
  // better-auth's default maxPasswordLength.
  passwordTooLong: "Use no more than 128 characters for your password.",
} as const;

/**
 * The practice record as one sheet (2026-09-22): each section's banner, in the
 * same small tracked label the panels carry everywhere else, and the words
 * around the coach's summary of the whole record. Nothing here is a rank.
 */
export const RECORD = {
  title: "Practice record",
  skillsExplained: "the six skills, explained.",
  summary: "In summary",
  summaryFrom: "from AJ",
  summaryWaiting: "AJ is reading the record.",
  summaryFailed: "AJ's reading could not be fetched. It is still there; try again in a moment.",
  summaryGated: "Finish onboarding and AJ will read the record here.",
  who: "Who this is",
  engagements: "Engagements",
  techniques: "Techniques demonstrated",
  feedback: "Feedback over time",
  shape: "The shape of it",
  nextSteps: "Worth trying next",
  reflection: "Today's reflection",
  coach: "Ask about the record",
} as const;

export const APP_NAV = [
  { id: "home", label: "Home." },
  // One place a learner returns to between cases (prompt 10): the old
  // Profile and Coach destinations merged. `coach` stays a ROUTE alias so old
  // links keep working; it no longer needs its own door in the nav.
  { id: "profile", label: "Practice Record." },
  { id: "library", label: "Case Library." },
  { id: "collaborate", label: "Collaborate." },
] as const;

/** The header's last word, for a signed-in account (2026-09-23): not a destination, so not in APP_NAV. */
export const APP_NAV_LOG_OUT = "Log out.";

/**
 * The header's last word for a GUEST (2026-09-24), in Log out.'s place, and
 * the paper it opens. Every sentence is what the server does: a guest is a
 * record on our server that this browser's cookie reaches, deleted by the
 * hourly purge thirty days after it was made (`guests.expires_at`), and the
 * first request that carries both the cookie and an account's session moves
 * everything the guest owns onto that account (claimSession) — whether the
 * account was just created or already existed (apps/api/src/auth/identity.ts).
 * The account's own settings, overall notes and opening win over the guest's.
 * The copy is Codex's (docs/CODEX-DRAFT-GUEST-NOTICE-2026-10-09.md), verbatim;
 * `staysBehind` holds its closing line.
 */
export const GUEST_SESSION = {
  button: "Guest session.",
  kind: "guest session",
  title: "Sign in to keep your work.",
  body: [
    "You are playing as a guest. Your guest record is stored on our server. This browser's guest cookie is your way back to it. Clear that cookie or use another browser or device and you cannot reach the record as this guest. Clearing the cookie does not delete the record from our server.",
    "Unless you sign in from this browser first, your guest session and the work attached to it are deleted from our active database thirty days after the guest session was made. Coming back does not restart those thirty days. Access ends then; deletion runs hourly, so it can take up to one more hour.",
    "Log in or create an account from this browser before then to keep your saved work, in a new account or one you already have. Your case runs, what you wrote and found, results and feedback come with you. So do your profile and onboarding conversation, reflections and development history, conversations with AJ, AJ's notes on each case and case suggestions, practice exercises and chosen focus, including what you recorded about using them. Your saved cases and the record of reading your development plan come with you too. This work becomes account data and is no longer subject to the guest's thirty-day deletion.",
  ],
  staysBehind: "Your settings and AJ's overall notes and opening message come with you unless the account already has its own. In that case, the account's versions are kept instead of the guest's. Saved cases from both are kept.",
  logIn: "log in.",
  createAccount: "create an account.",
} as const;

export const PLAY = {
  inputPlaceholder:
    "Ask clarifying questions, request documents, describe your mental framework, or attempt an action.",
  /**
   * The first three turns of any engagement ask for the shape of a move in
   * the placeholder (onboarding pass, 2026-09-13, idea 7): what you think,
   * what you have seen, what you will try. After that the standing
   * placeholder returns. Never a rule, only a prompt.
   */
  firstTurnPlaceholders: [
    "What do you make of it, what have you seen that makes you think so, and what will you do first?",
    "Say what you are thinking, what it rests on, and the move you want to make.",
    "Your read, the evidence for it, and what you will try next.",
  ],
  /** The mentor's opening ask, rendered under the authored opening in his own voice; {mentor} is his name from the case. */
  mentorAsk:
    "{mentor} wants one thing before you touch any of it: tell him what you make of it, what you have seen that makes you think so, and what you will do first.",
  next: "next",
  walkBackIn: "walk back in.",
  /**
   * THE BOOK'S FIRST PAGES (2026-09-22): a playthrough opens on the case's
   * title page, then the prologue, then the story. The title page's one
   * affordance, in the frames' voice; the prologue's running label; and the
   * scene caption while a prologue page is open.
   */
  turnThePage: "turn the page.",
  titleEyebrow: "Real Case Simulations",
  prologueCaption: "Prologue",
  settings: "settings",
  /** U+25BD, as drawn in frames 6/8/11. */
  settingsCaret: "▽",
  /** U+25B7, as drawn in frames 9/10. */
  nextCaret: "▷",
  attach: "+",
  /** The send control. Bottom-right of the composition bar, messenger-style. */
  send: "send",
  /** U+2192. */
  sendCaret: "→",
  sendLabel: "Send what you have written",
} as const;

/**
 * Copy for surfaces the frames do not contain. Written in the frames' voice
 * (lowercase affordances with a trailing period; no exclamation; no
 * encouragement) but authored here, not extracted — see
 * docs/DESIGN-SYSTEM.md section 7.
 */
export const PROPOSED = {
  notYet: "not yet.",
  submitRecommendation: "submit recommendation.",
  present: "present.",
  recommendationHint: "This ends the working phase. You will be asked about it.",
  debriefTitle: "The tape.",
  debriefTruth: "What was actually true.",
  debriefCounterfactual: "What you would have found.",
  debriefRecord: "Every check, in order.",
  debriefDecoy: "The story everyone believed.",
  debriefAnalysis: "Where it turned.",
  debriefScorecard: "How the work was read.",
  debriefNarrated: "Where you first told the story.",
  debriefNarratedLead: "The first move that said what you thought, what you had seen, and what you would do:",
  debriefNarratedNever: "No single move in this engagement said all three things: what you thought, what you had seen that made you think it, and what you would do. Try it from the first turn next time; the people in the room answer the thinking, not the request.",
  crossExamTitle: "In the room.",
  crossExamWaiting: "She is considering that.",
  debriefEnding: "The deadline came first.",
  debriefEndingLead: "The meeting went ahead before you presented a recommendation. These threads were still open when it did:",
  debriefEndingAtDeadline: "You presented at the deadline.",
  debriefEndingAtDeadlineLead: "The days ran out and you presented with what you had. These threads were still open when you did:",
  engagementComplete: "The engagement is over.",
  engagementWritingUp: "The write-up is being prepared. It takes a minute.",
  toDebrief: "read the debrief.",
  auditTitle: "The full record.",
  notEnoughHistory: "not enough history yet.",
  coachStanding: "AJ reads the assessment record. It cannot change a score.",
  connectionLive: "live",
  connectionReconnecting: "reconnecting",
  connectionLost: "lost",
  retryTurn: "retry this turn.",
  sessionMoved: "the session moved on, reloading",
  turnTimedOut: "This turn has been running for longer than it should. Nothing was lost — you can retry it.",
  /**
   * The microphone (ADR 0011). The frames draw the icon and nothing else, so
   * its states are authored here: each is a plain sentence about what is
   * happening, never a status code.
   */
  speak: "speak.",
  speakLabel: "Speak instead of typing. What you say lands in the box for you to read and change before you send it.",
  micAsking: "asking for the microphone.",
  listening: "listening. tap to finish.",
  transcribing: "writing it down.",
  micDenied: "the microphone is off in your browser.",
  micFailed: "that did not come through. try again.",
  /** The narration voice, one control per turn. */
  listen: "listen.",
  listenLabel: "Read this turn's narration aloud",
  stopListening: "stop.",
  fetchingVoice: "finding the voice.",
  voiceFailed: "the voice did not come through.",
  readAloud: "read aloud",
  readAloudHint:
    "Off by default. Turning this on reads each turn's narration aloud as it arrives. Every turn also has its own listen control, whichever way this is set.",
  readAloudUnavailable: "There is no voice on this server yet.",
} as const;

/**
 * DB-IP'S CREDIT (the region gate, Andre's rulings of 2026-10-27). The country
 * table is DB-IP Lite, CC BY 4.0, and DB-IP asks a web application to link back
 * to it on pages that display or use results from the database
 * (apps/api/data/geo/README.md). Carried on the privacy page and on the region
 * gate's screen. The link words are DB-IP's own, with "geolocation" lowercased
 * as Andre's ruling writes it.
 */
export const DB_IP_CREDIT = {
  // CODEX: the credit's words are DB-IP's attribution; keep "DB-IP" and the link.
  link: "IP geolocation by DB-IP",
  // A link the licence asks for, not an endpoint: nothing here fetches it.
  // eslint-disable-next-line no-restricted-syntax
  href: "https://db-ip.com",
} as const;

/**
 * THE REGION GATE'S SCREEN (Andre's rulings of 2026-10-27 and answers of
 * 2026-10-28): the api refused a door that writes or calls a model (a new
 * guest, sign-up, AJ's intake, the coach, practice, dictation, read-aloud, a
 * turn, a case) from the EU, the EEA or the UK with 451. One plain screen, in
 * place of the app, until the page is reloaded: no retry, no door to try
 * again. It names no country: the api does not say which one it found. What
 * stays open from there is on it: the privacy and terms pages, the learner's
 * settings (the export, the deletion, the email choices) and logging out for
 * someone who has something kept here, and signing in for someone who does not.
 */
export const REGION_NOT_OFFERED = {
  // CODEX: Claude's plainest true words for the screen; Codex to write them.
  title: "Not offered here yet.",
  // CODEX: the screen's one sentence, as the ruling words it.
  body: "Pushback & Chaos is not offered in your region yet.",
  // CODEX: the support line; the address is support@pushbackandchaos.com, linked.
  contact: { before: "For questions, write to ", address: "support@pushbackandchaos.com", after: "." },
  // CODEX: the line over the doors that stay open, for someone with something kept here (a guest or an account).
  keptLine: "What you have here is still yours to download or delete.",
  // CODEX: the door to the settings sheet (the export, the deletion, the email choices).
  settings: "your settings.",
  // CODEX: the account's log-out door, as the shell's.
  logOut: "log out.",
  // CODEX: the line for someone with nothing kept in this browser, over the sign-in door.
  signInLine: "Have an account from elsewhere? You can still sign in to download or delete it.",
  // CODEX: the sign-in door.
  signIn: "sign in.",
  // CODEX: the two reading pages' doors.
  privacy: "privacy.",
  terms: "terms.",
  credit: DB_IP_CREDIT,
} as const;

/**
 * The privacy policy, rendered verbatim by screens/privacy.tsx.
 *
 * RULES FOR EDITING THIS BLOCK. Every sentence here must be true of the
 * running system TODAY, not of the system as intended. When an operational
 * fact changes (a provider grants zero retention, hosting moves, a backup
 * schedule starts), this text changes in the same release, and `updated`
 * changes with it. Aspirations belong in the roadmap, not here.
 */
/**
 * Visit counting (ADR 0022) changed the page on October 9. The arrival book
 * (2026-10-11) changes it again: the removal sentence now names the intake, the
 * rehearsal and the quotes, which "remove it all." removes too. The date must
 * be the real deploy date (see ANALYTICS-A-RECORD.md and the email-choices
 * record); October 11 was the earliest it could ship. It shipped on October 13,
 * with the account export and the track sentence (release runbook 2026-10-13, Y1).
 */
// Coach Release E (2026-10-20): the coach's replies and its greeting move to GLM 5.3 through
// Perplexity, so the providers paragraph names Perplexity and Z.ai, what is sent to them and when,
// and that no retention period has been stated (Perplexity offers no zero retention for models made
// by others; Andre, 2026-10-20: proceed with that uncertainty). It ships in the same deploy as the
// switch, never before or after, and the date is that deploy's.
// Account erasure (2026-10-20; docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md, "Privacy page") changes
// "Email from us" and "How long we keep it" below, and ships on 2026-10-21 (Andre: merge and
// deploy), so this line, and contracts' PRIVACY_POLICY_VERSION with it, is that deploy's date
// (ADR 0021, "before the change takes effect"; docs/ACCOUNT-ERASURE-RECORD.md). Sending switched
// on later the same day (Codex's words for "email is now sent"; docs/cme/MAIL-RECORD.md), in a
// release deployed on 2026-10-21, so the date was unchanged: three texts shared it. Provider
// unsubscribes (what a mail app's Unsubscribe or a spam report does) deploy on 2026-10-22 (UTC).
// The OpenAI retention correction (Andre, 2026-10-27) changes three sentences below (the providers paragraph, dictation and read-aloud); this line is
// set at that correction's deploy, not here.
// The encryption window (2026-10-28) wrote two more ("Where it lives." and the backups sentence,
// which the deletion dialog and the terms share). Andre HOLDS both for the release after
// 2026-11-05, so they are back to the live words here (marked HELD: below, each with the held
// sentence); the same date rule holds when they ship.
// The Z.ai switch (Andre, 2026-10-29: "We are bypassing Law 5, and switching production to our Z.ai
// key.") rewrites the providers paragraph: the coach's replies and greeting go to Z.ai directly, what
// each sends, that no agreement of ours stands behind Z.ai, what Z.ai's own published terms say, and
// the requests that went through Perplexity before (Codex's words, 2026-10-29;
// docs/COACH-ZAI-DIRECT-2026-10-29.md). It ships in the same deploy as the switch, and this line, with
// contracts' PRIVACY_POLICY_VERSION, is that deploy's date (2026-10-31, UTC; set first at the
// earliest day it could ship, 2026-10-30, and moved when the deploy's switchover moved past 00:00
// UTC), with the terms' date and version (Andre's ruling 3), and moved with the pins if the deploy
// is later still.
export const PRIVACY_POLICY_VERSION = "Last updated October 31, 2026.";

export const PRIVACY = {
  title: "Privacy.",
  updated: PRIVACY_POLICY_VERSION,
  intro:
    // The short version narrowed for learner totals (Codex, docs/cme/CODEX-DRAFT-ANALYTICS-B-2026-10-08.md, section 1).
    "This page says exactly what happens to your data, in plain language. The short version: your session content is for you and your coach. Nothing you write is ever used to train AI, and nothing is ever sold or shared for advertising.",
  sections: [
    {
      // THE ACCOUNT RELEASE (2026-10-22): the controller line, docs/TERMS-DRAFT-2026-10-22.md
      // Appendix B1, a new first section (the page had no controller and no contact). Its last
      // sentence, the link to the terms, is `termsLine` below, rendered under this section.
      // Words reviewed by Codex, 2026-10-22.
      heading: "Who is responsible.",
      body: "Fleuri Consulting Group runs Pushback & Chaos. It is the data controller: it decides how your data is used here and is responsible for it. For questions or requests about your data, write to support@pushbackandchaos.com.",
    },
    {
      heading: "What we collect.",
      body: "If you create an account: your name, your email address, and a password (stored only as a hash) — or, with Google sign-in, the name and email Google provides. If you play as a guest: only a random identifier, no name or email. In both cases we keep what you produce while playing: everything you type, the scenario events and documents your play generates, your results, your reflections, and your conversations with the coach. Your coach also keeps its own notes about your practice — how you tend to work, what you do well, what to work on next, and what it has already suggested — written from your results after each finished case, so that it is the same coach every time you return; and, if you use them, your practice exercises and the focus you chose, with what you told the coach about using it. We also keep routine operational logs (timestamps, request outcomes) that do not contain your session text.",
      more: [
        "If you choose to tell us, we also keep the kind of program you're recruiting from: undergraduate, MBA, another graduate program, or none. We also keep the recruiting season you're aiming for. Both are optional. You can change or clear either in settings. Nothing in a case depends on them.",
        // THE ACCOUNT RELEASE (2026-10-22): docs/TERMS-DRAFT-2026-10-22.md Appendix B2, with the
        // bracketed [30] filled from AUTH_MAIL_SENDS_RETENTION_DAYS (packages/db, migration 0048).
        // The send record (auth_mail_sends) holds a row for a send the per-account limit or the
        // switch held back too, so "or hold one back" and "whether it went" are added to the
        // draft's sentence. Both tables cascade with the account. Words reviewed by Codex, 2026-10-22.
        // Pre-release review: a link confirming an account whose sign-in was not the confirmer's
        // adds a terms note (account, version, time only), kept until account deletion. Ordinary
        // confirmation of one's own email sign-up adds none. Appendix B2 mirrors the new sentence.
        "When you create an account, we record the version of the Terms you accepted and when. This lets us ask again when the terms change. If you confirm an address whose sign-in you didn't set up, we add a note to that record and ask you to accept the Terms again. We record whether your email address is verified. For each verification or password reset email we send or hold back, we record the account, the kind of email, the outcome and the time. That record never contains your email address, the link or the message. We use it only to limit sends. We delete it after 30 days, or sooner if your account is deleted.",
      ],
    },
    {
      heading: "The AI providers who process it.",
      // The processor, OpenAI and Anthropic sentences are Codex's words (docs/CODEX-DRAFT-OWED-LINES-2026-10-27.md,
      // section 11; PR #165); the Z.ai switch's words below changed none of those four sentences, so both stand.
      // The OpenAI sentence (Andre, 2026-10-27): no zero-retention agreement with OpenAI is recorded
      // (only API logging and sharing were disabled in its console, 2026-09-01), so the page says what
      // is true: up to 30 days for abuse monitoring, no agreement. The Z.ai switch (Andre, 2026-10-29),
      // in Codex's words of 2026-10-29, which also are the owed voice pass on this paragraph: what the
      // reply and the greeting each send (apps/api/src/routes/coach.ts and coach-home.ts), and Z.ai's
      // position as its own pages state it, read 2026-10-29 (docs.z.ai Terms of Use, "Our Use of User
      // Content" item 7; the Data Processing Addendum for API Services, sections 3(a) and 4(b)).
      // Round two (the review, 2026-10-29; Codex's words): Z.ai's own Context Caching guide says it
      // caches repeated prompt content automatically for a period it does not state, and our
      // comparison read 7,808 of 7,853 tokens back as cached 108 s later, so the paragraph no longer
      // repeats the DPA's "not stored". It gives Z.ai's terms whole (Additional Terms for API Services
      // 3(b): law, policy and abuse; Terms IV.3(b): usage and technical data), and the greeting's
      // list names the learner's words on the focus (coach-home.ts sends renderCoachFocus's block).
      // The cross-examination sentence ("When you present your recommendation, ..."; HB-10, Andre's
      // answer 12, 2026-10-28), Codex's words of 2026-10-28: Doris's questions carry the name
      // on the learner's confirmed profile (none for a guest without one) so that she addresses the
      // learner by it or by nobody, so that name reaches Anthropic in the cross-examination's prompt.
      // It ships in the same release as that change (PR #170), placed after what a session sends,
      // inside main's Z.ai paragraph, whose other words it leaves as they are; the date line is the
      // one deploy's (October 31, 2026), with PRIVACY_POLICY_VERSION.
      body: "Scenario play runs on Anthropic (Claude) and skill evaluation runs on OpenAI. Your coach's replies, and the greeting it writes when you arrive at your desk, run on GLM, a model made by Z.ai. We now send those requests directly to Z.ai, not through Perplexity. The rest of the coach's work, including the conversation in which you met and its notes, runs on Anthropic. What you write during a session is sent to Anthropic to run the scenario; a limited summary of your declared moves is sent to OpenAI to evaluate them. When you present your recommendation, the partner uses the name on your profile to address you during questioning, so that name is also sent to Anthropic. When you ask the coach something, we send Z.ai your profile, your finished results and the words of yours they quote, relevant excerpts from finished engagements, the coach's notes, your recent conversation and question, and your practice exercise and focus with what you wrote there. We also send your desk's cards and the published case list. A new greeting when you open your desk uses a smaller set: parts of your profile, your finished results and the words of yours they quote, the coach's notes, your plan's next steps, your practice focus with what you wrote there, and your desk's cards. These providers act as processors. OpenAI may keep what we send for up to 30 days to monitor for abuse; we have no zero-retention agreement with OpenAI. Under its standard terms, Anthropic may keep API traffic for a limited period to monitor for abuse, then deletes it. We access Z.ai's models through their API. Under Z.ai's API terms, they don't use API content to develop or improve their services unless the customer explicitly agrees, and we have not agreed. Z.ai's terms also allow it to use what we send as needed to comply with the law, enforce its policies and prevent abuse. They let it collect usage and technical data about our use, which may include inferences, and use that data to improve its services. Its API data terms say data is generally processed in Singapore. Z.ai automatically keeps repeated parts of what we send, which can include your record, to reuse them; it has not said for how long. Earlier coach requests went through Perplexity; Perplexity and Z.ai may keep those under their own terms for a period neither has stated to us. Our no-training statements about Z.ai rely on its published terms alone. None of them uses it to train models.",
    },
    {
      heading: "Your voice, if you use it.",
      // Codex's words (docs/CODEX-DRAFT-OWED-LINES-2026-10-27.md, section 11). The dictation sentence (Andre, 2026-10-27): the recording goes to
      // OpenAI under the same retention as the Assessor's input, so "not by OpenAI" is replaced. The
      // read-aloud sentence (the review, 2026-10-27): the narration text and the audio it comes back
      // as go to the same account under the same retention; we store no audio (the route sends it
      // `no-store`), so "is not stored" is true of us alone and now says so.
      body: "The microphone is optional and stays off until you press it. When you dictate, OpenAI turns the recording into text, which appears in your box for you to read and change. We do not keep the recording; OpenAI may keep it for up to 30 days to monitor for abuse, under the terms described above. When you turn on read-aloud, we send each turn’s narration to OpenAI through the same account used for dictation, and the returned audio plays in your browser. We do not store that audio; OpenAI may keep both the narration and the audio for up to 30 days under the same terms. Nothing about how you sound is ever measured or scored.",
    },
    {
      heading: "Where it lives.",
      // Codex's storage paragraph (docs/CODEX-DRAFT-GUEST-NOTICE-2026-10-09.md): the browser keeps
      // cookies, the session and profile in view, the desk layout and unsent drafts.
      // HELD: Andre holds the encryption sentence for the release after 2026-11-05 (the Z.ai
      // switch's record, docs/COACH-ZAI-DIRECT-2026-10-29.md, "The held sentences"). When it ships
      // it goes after "(TLS).", as written on 2026-10-28 and marked for the voice pass: "Since
      // October 28, 2026, the database, its backups and the service's secrets have been stored
      // encrypted at rest on the server." Why it waits (the deployment record in
      // docs/RUNBOOK-DISK-ENCRYPTION-OPTION-B-2026-10-28.md): the database, its nightly dumps, .env
      // and the container runtime's store (each container's environment) sit in a LUKS2 container on
      // the server's disk, which Andre unlocks after each boot. "The service's secrets", not the
      // server's: sshd's host keys stay on the plain disk. It is true of every copy only once phase
      // 4 has removed the plain copies and overwritten the disk's free space (4.2 and 4.3,
      // 2026-10-29) and every hosting-provider image taken before that wipe has aged out (7 days
      // after it, about 2026-11-05) or been deleted: ship it no sooner, or say so.
      body: "Your saved practice record is stored on our servers in Helsinki, Finland (EU), and moves over encrypted connections (TLS). Your browser keeps cookies for access, including the guest cookie that lets you return to your guest record. It also remembers which session and profile you were viewing, your desk layout and unsent drafts. Those drafts and layout are local to this browser; signing in transfers the record saved on our server. If you turn off visit counting below, this browser also keeps that preference.",
    },
    {
      heading: "Who can see it.",
      // Two sentences narrowed for learner totals (Codex, docs/cme/CODEX-DRAFT-ANALYTICS-B-2026-10-08.md, section 1).
      body: "You, through the app. Your coach, which reads your finished results and its own notes to advise you. And the people who operate this service, who can technically reach the database but access individual records only to run, debug, and secure it — never to browse sessions, and never for any commercial purpose. We are honest about the limit: coaching runs on our server, so the server can read your data, and what we can promise is that no person browses your sessions, your session content goes only to you and the processors described on this page, nothing is sold, and access is logged — not that access is impossible. There is no third-party analytics, no ad network, and no data broker anywhere in this product.",
    },
    {
      // Codex's section 1 (docs/cme/CODEX-DRAFT-ANALYTICS-B-GROUPS-REMOVED-2026-10-09.md), in the
      // release that shows learner totals (ADR 0021's Zone B, migration 0039).
      // 0044 counts case tracks, with all grouped publication held under ADR 0023.
      heading: "Counting case use.",
      body: "The people who run the service see site-wide totals of ten learners or more about how cases are used: how many start, finish, receive their first feedback report, sign up, or come back. Counts are rounded to the nearest five. These totals never show who did what or anything anyone wrote, and they are never linked to visit counting. Smaller counts are hidden, and some larger counts are hidden too to protect the smaller ones. We also count first starts and finishes by case track, and the first time a learner finishes engagements in two or more tracks. These breakdowns are withheld while their disclosure controls are under review.",
    },
    {
      // Codex's section 1 (docs/cme/CODEX-DRAFT-ANALYTICS-A-2026-10-08.md), in the release that starts
      // counting (ADR 0022). The opt-out control (VISIT_COUNTING) renders after this section.
      heading: "Counting visits.",
      body: "We count visits to see how this app is being used: the routes visited, the referring site's name, campaign tags in the link you arrived through, and a broad device type, such as phone or computer. Routes contain no personal IDs; we keep no other details from the link.",
      more: [
        "Counting happens on our own server. It uses no cookies and stores nothing on your device. For counting, we remove the end of your IP address first, then make a daily code. That code cannot link one day's visits to the next, and visit records are never linked to your account or anything you write here.",
        "Only the people who run the service see these counts, and only as totals. Groups with fewer than ten visitors are hidden. We keep the raw visit records for 13 months, then delete them; totals are kept.",
        "You can ask us not to count your visits. We honor Global Privacy Control and Do Not Track, or you can use the control below. The control saves only a “do not count” preference in this browser; that preference is never sent to us.",
      ],
    },
    {
      // Codex's section 4 (docs/cme/CODEX-DRAFT-EMAIL-CHOICES-2026-10-07.md), updated for
      // chosen and account-deletion emails sending in the next release (2026-10-21).
      // The sending-hold sentences are removed; Mailjet's role and storage are present tense.
      // Our unsubscribe links cover the chosen emails; a mail app may offer its own on deletion emails.
      // The policy date is set with the release by Claude.
      // Stop all (2026-10-21): the body's last sentence is scoped to the email choices (it was "We
      // send only the emails you choose here, to a verified address.", which the deletion emails,
      // sent to an address verified or not, contradicted). The card says "here"; this page does not.
      // Wording reviewed by Codex, 2026-10-21.
      heading: "Email from us.",
      body: "Creating an account adds you to no email list. Email choices become available after your first Harborlight feedback report. Once you're signed in, you can choose emails on the card on your desk or in settings. Every box starts empty. You can choose none. The emails you choose go only to a verified address.",
      more: [
        "The choices are an email when a new case is released; an email when a major update ships; a weekly note from AJ, all year or only during your recruiting season; an email when the people who made Pushback & Chaos release something new with a free demo; reminders as your recruiting season approaches and opens; invitations to give feedback or join a short interview; and one email after recruiting season. A major update email goes out only when Andre decides an update warrants one.",
        "Email is sent by Mailjet, which acts as our processor and stores your email address in the European Union. If you choose a weekly note from AJ, the email contains your own coaching content: what you've worked on and what to try next. Mailjet stores that content in the European Union too. Mailjet receives your email address and the message. If you use your mail app's unsubscribe button or mark an email as spam, Mailjet also receives that action, with your IP address, country and mail client, and keeps your address as unsubscribed under its own terms. We use only your address and the opt-out; we do not store your IP address, country or mail client. We use no open pixels or rewritten links.",
        "Certificates and feedback reports or results have separate email choices. We'll ask just before we would first email each kind. Saying yes will allow that kind of email from then on. Choosing updates will not select either of these for you. Choosing feedback invitations will not give us permission to use your sessions for research; that permission is asked separately.",
        // Stop all (2026-10-21): the link's page can now stop every email choice at once (POST
        // /u/:token/all), so the sentence after the link's promise says so.
        // Wording by Codex, 2026-10-21: every choice, including certificates and results.
        "Once the choices are available and you're signed in, you can change them or choose no update emails on the desk card or in settings at any time. Every email we send for these choices has an unsubscribe link that works without signing in. On that page, you can stop that kind of email or turn off every email choice at once. Withdrawing a choice in the product or using our link stops emails for that purpose at once. Using your mail app's unsubscribe button or marking an email as spam turns off every email choice through Mailjet, usually within minutes. Mailjet retries for up to a day if we are unreachable. All opt-outs take effect no later than ten business days.",
        "The email after recruiting season will ask whether you interviewed, how far you got, whether you had an offer, and whether practice here helped. Answering will be optional. Your answers will be kept with your account and counted only in totals of ten people or more. No individual answer will be shown to anyone, including any club or school.",
        // Account erasure (2026-10-20), words by Codex, 2026-10-21, written to what the worker
        // does (apps/worker erasure-mail.ts): the confirmation to the account's address whether or not
        // it is verified, the note when the account is kept, the copies only to a verified address and
        // only as chosen, Mailjet as the sender, and no unsubscribe link of ours. Updated for sending on
        // and provider unsubscribes: a mail app may still offer one, which turns off the email choices.
        "Account deletion has its own emails, separate from your email choices: a confirmation with the deletion date to your account address, verified or not, and a notice if you choose to keep your account. Only a verified address can receive copies of your coaching record and completed case books, and only if you ask for them. Mailjet sends these emails for us and stores any copies they contain in the European Union. These emails are not marketing and have no unsubscribe link of ours. Your mail app may still offer one; using it turns off every email choice.",
        // THE ACCOUNT RELEASE (2026-10-22): the two security emails, docs/TERMS-DRAFT-2026-10-22.md
        // Appendix B3, its two bullets written into the first paragraph ("Verification:" and
        // "Password reset:") because this page has no lists. Sent by the api at request time from
        // PNC_ACCOUNT_MAIL_FROM ("Pushback & Chaos <support@pushbackandchaos.com>") through Mailjet,
        // under PNC_TRANSACTIONAL_SEND. The lifetimes are VERIFICATION_LINK_TTL_SECONDS and
        // PASSWORD_RESET_LINK_TTL_SECONDS (packages/mail); the bracketed limits are Claude's default
        // per-account caps (one a minute and five a day, for each kind): hold them equal to the caps
        // as built. A spam report withdraws every choice for the address whichever message it came
        // from (apps/api/src/routes/mail-events.ts). Codex (2026-10-22): the heading covers both
        // chosen and security emails. A repeated unconfirmed sign-up sends a set-password link only
        // for an account with only password sign-in or no way in; the short sentence names the former.
        "Your account also has two security emails. They are sent regardless of your email choices. Verification: we send a confirmation link when you sign up with an email and password, ask for another link, or sign in with your password before confirming your address. Password reset: we send a link when you ask to reset your password, if your account has a password or has no way to sign in. We also send this link if you sign up with an address that already has an unconfirmed account that uses only a password, so you can set its password.",
        "Both come from Pushback & Chaos <support@pushbackandchaos.com>, and Mailjet sends them for us. Mailjet receives your address and the message, including the link. A verification link lasts 24 hours. A reset link works once and lasts one hour. Each account can receive at most one email of each kind a minute and five of each kind a day. These emails are not marketing and have no unsubscribe link of ours. Marking one as spam or using your mail app's unsubscribe button turns off every email choice, just as it does for deletion emails.",
      ],
    },
    {
      heading: "Training and research.",
      // The second sentence narrowed for learner totals (Codex, docs/cme/CODEX-DRAFT-ANALYTICS-B-2026-10-08.md, section 1).
      body: "Your sessions are never used to train AI models — not by us, not by our providers. For Z.ai, that statement rests on its published terms alone, which we cannot verify. Your session content is also never used for our own research or product development unless you separately and explicitly opt in; the default is no, and staying opted out changes nothing about your experience.",
    },
    {
      heading: "How long we keep it, and how to delete it.",
      // Codex's retention paragraph (docs/CODEX-DRAFT-GUEST-NOTICE-2026-10-09.md), then the last
      // sentence, Codex's too (review 5; ADR 0023, revision 4): learner totals are counters that a
      // deletion never reduces, so "everything attached to it" needs saying. The removal sentence
      // widened by Claude for accuracy, 2026-10-11 (the intake conversation, the rehearsal move and
      // the quotes go with "remove it all." too; the profile stays); Codex to review. The account
      // download sentence is the account export's (2026-10-12).
      // Account erasure (2026-10-20), words by Codex, 2026-10-21: the five sentences from "To delete
      // your account" to "never anything you wrote." replace "email
      // support@pushbackandchaos.com from your account address and it will be removed." They say
      // what the code does: the settings door (accounts only), every sign-in deleted on asking,
      // erase_after 30 days out (0046 holds it), keeping it by signing back in and choosing to, the
      // hourly purge at :40, the nightly backups' 14-day prune, and the notice to PNC_SUPPORT_EMAIL
      // on request and on keep, which carries the address, the dates and the copy choices only.
      // Sending-on release: support notices are sent; deletion cannot reach provider-held copies.
      // Stop all (2026-10-21): the requested notice also prints whether the address was verified
      // (packages/mail/src/templates/erasure.ts, "Address verified at request"), so the sentence
      // names it. "Whether it was verified" reviewed and kept by Codex, 2026-10-21.
      // HELD: Andre holds the backups sentence for the release after 2026-11-05 (the Z.ai switch's
      // record, "The held sentences"), so it reads as live. When it ships, "Backups that still hold
      // your account age out within 14 days of the deletion." becomes, as written on 2026-10-28 and
      // marked for the voice pass: "Backups that still hold your account age out within about 21
      // days of the deletion, because our hosting provider keeps its backups of our server for 7
      // days." Why (D8 overridden, Andre, 2026-10-28): the hosting provider's server backups
      // (Hetzner Backups, each image kept 7 days) hold the encrypted container, dumps included, so a
      // copy can outlast our own dumps' 13 to 14 days by 7. Counsel is asked whether 21 days is
      // acceptable (docs/COUNSEL-BRIEF-2026-10-27.md, item 3). ERASURE.explanation and the terms'
      // "When it ends." carry the same sentence and are held with it (copy.test.ts).
      body: "Guest sessions and all the work attached to them are deleted from our active database automatically 30 days after the guest session was made, unless you sign in from the browser holding that guest session before it expires. Returning does not extend the 30 days. Guest access ends at expiry; deletion runs hourly, so it can take up to one more hour. Signing in transfers your saved guest work to your account, including your case runs and results, profile and onboarding conversation, reflections and development history, coach conversations, case notes and suggestions, practice exercises and focus, and saved cases and development-plan reading record. Existing account settings and the coach's overall notes and opening message take precedence over guest versions; otherwise those transfer too. Account data is kept while your account exists. Everything the coach keeps about you — the conversation, its notes, your exercises and your focus, the conversation in which you met, your rehearsal move and the words your profile was read from — you can take a copy of, or remove whole, from settings at any time; your profile, your engagements and their results stay. You can also download a copy of your account from settings, including while you are a guest; the file lists anything it holds back and why. To delete your account and everything attached to it, choose \"delete my account.\" in settings while signed in. Confirming signs you out on every device and schedules deletion 30 days after you confirm; before then, you can sign back in and choose \"keep my account.\" Deletion runs hourly, so it can take up to one more hour. Backups that still hold your account age out within 14 days of the deletion. Copies the AI providers or Mailjet already hold under their own terms are outside our reach. We also send a notice to our support address when you request deletion and another if you keep your account; these contain only your account address, whether it was verified, the dates and your copy choices, never anything you wrote. Site-wide totals contain no account or guest identifier and are not reduced when your account or guest play is deleted.",
    },
    {
      heading: "Changes.",
      body: "If any of the above changes, this page changes, with a new date at the top — before the change takes effect, not after.",
    },
  ],
  backToSignIn: "back to sign in.",
  // The review of 2026-10-22: the page's last control goes back to where the reading began, so it
  // says "back to sign in." only when that is the sign-in card, and this anywhere else (terms' word).
  back: "back.",
  // The controller section's last sentence (B1), reviewed by Codex; "Terms" links to #/terms.
  termsLine: { before: "Using the service is also covered by our ", link: "Terms", after: "." },
  // THE REGION GATE (Andre's rulings of 2026-10-27 and answers of 2026-10-28; apps/api/src/geo/):
  // rendered as the last paragraph of "What we collect.", the credit linked to DB-IP (CC BY 4.0).
  // True of the code: the lookup runs in the api's memory on our server at every door that writes
  // or calls a model (a counted visit, starting as a guest, signing up, a case, the coach,
  // practice, dictation, read-aloud; geo/region-policy.ts) and when a debrief or casebook is read
  // (to hold the coach's note), and it stores nothing; while the gate is on the doors that can refuse log no address,
  // so no log line pairs an address with its answer. "for this" is exact: every other route's
  // request line carries the address, which the page's own sentence on logs does not yet say
  // (open for Andre). The date line moves at deploy.
  regionCheck: {
    // CODEX: Claude's plainest true sentence; Codex to write it. The credit after it is DB-IP's.
    before: "Whenever the app saves something or asks one of our AI models (a counted visit, starting as a guest, creating an account, a case, the coach, practice, dictation or read-aloud), our own server checks your IP address against a country table to see whether the service is offered where you are. Your IP address is not kept for this. ",
    credit: DB_IP_CREDIT,
    after: ".",
  },
} as const;

/**
 * THE TERMS PAGE (2026-10-22, Andre's decisions for the release that ships
 * hard verification and password reset), rendered verbatim by
 * screens/terms.tsx.
 *
 * Interim text, true today; counsel to review before any payment is taken.
 * Words reviewed by Codex (2026-10-22), mirrored in docs/TERMS-DRAFT-2026-10-22.md.
 * "When it ends." (T11) keeps the draft's 14 days while Andre holds the about-21-days
 * backups sentence for the release after 2026-11-05 (HELD: there).
 * Appendix A sources the facts. Counsel still reviews before anything is sold.
 * Change words, never facts.
 * The editing rule is PRIVACY's: every sentence must be true of the running
 * system today, and when one stops being true this block changes in the same
 * release, with `updated`. A change that matters to people's rights also asks
 * every account to accept again, which is what moving contracts'
 * TERMS_VERSION does (GET /me/identity's `termsCurrent`); any other edit moves
 * only TERMS_UPDATED. No payments section: nothing is sold.
 *
 * The draft and page share the words, including US$50 without brackets and the request for acceptance without
 * blocking use. Andre approved the text and kept
 * US$50 (2026-10-22). "Email." renders its three items as `list`.
 *
 * The page's date line is contracts' TERMS_UPDATED, which may move for any
 * edit; the version an account accepts is contracts' TERMS_VERSION, moved
 * only for a change that matters to people's rights (the review of
 * 2026-10-22 split the two).
 */
// The date is set at deploy, with contracts' TERMS_UPDATED (a test holds the two equal). The Z.ai
// release (Andre's ruling 3 of 2026-10-29) moves it and contracts' TERMS_VERSION together, so every
// account accepts again once: the earliest day it can ship, moved with the pins if it ships later.
export const TERMS_UPDATED = "Last updated October 31, 2026.";

export const TERMS = {
  // Codex voice pass, 2026-10-22; mirrored in docs/TERMS-DRAFT-2026-10-22.md.
  title: "Terms.",
  updated: TERMS_UPDATED,
  intro:
    "These terms are our agreement for using Pushback & Chaos. The short version: free practice on invented business cases with an AI coach. What you write is yours. Don't copy the cases or artwork, and don't try to break in. Nothing here is professional advice.",
  sections: [
    {
      heading: "Who we are, and what you agree to.",
      body: 'Pushback & Chaos is run by Fleuri Consulting Group ("we" and "us"). These terms are the agreement between you and us for using Pushback & Chaos: the app, its cases and documents, and its coach (together, "the service"). By continuing as a guest, with Google, or with an email address and password, you agree to them. If you don\'t agree, please don\'t use the service.',
      more: [
        "When you create an account, we record your acceptance of these terms, the version and the time. If your account has no acceptance of the current version on record, we ask you to accept it in the product. These terms also cover guest play. We don't keep a separate acceptance record for guests.",
        "The Privacy page explains what happens to your data and forms part of this agreement.",
      ],
    },
    {
      heading: "Who can use it.",
      body: "You must be 18 or older to use the service. By using it, you confirm that you are at least 18. We don't ask for your date of birth. If we learn that someone under 18 is using the service, we may end their access.",
      more: [
        // THE REGION GATE (Andre's rulings of 2026-10-27 and answers of 2026-10-28): every door
        // that writes or calls a model is refused from the EU, the EEA and the UK
        // (apps/api/src/geo/). Mirrored in docs/TERMS-DRAFT-2026-10-22.md, T2. Andre, 2026-10-28:
        // only TERMS_UPDATED moves, at deploy; TERMS_VERSION stays, so no account is asked to
        // accept again (docs/DEPLOY.md, "Region gate"). It ships in the Z.ai release, which moves
        // TERMS_VERSION for its own reason (Andre's ruling 3 of 2026-10-29).
        // CODEX: Claude's plainest true sentence; Codex to write it.
        "The service is not offered in the European Union, the European Economic Area or the United Kingdom at present.",
      ],
    },
    {
      heading: "Accounts and guest play.",
      body: "You can play as a guest or with an account. A guest session holds no name or email. A cookie ties it to the browser you started it in, so if you clear that cookie or switch browsers, you can't get back to it. A guest session and everything in it are deleted 30 days after it was made.",
      more: [
        "You can create an account with Google, or with an email address and a password of at least 12 characters. To sign in with email and password, you must first confirm your address. We email you a link that lasts 24 hours. Open it, then press the button on the page to confirm your address and sign in on that browser. With Google, you sign in with the address Google provides.",
        "To bring guest work into your account, sign in from the browser holding the guest session before its 30 days are up. For a new email account, open the confirmation link in that browser and press the button on the page. If you confirm in another browser or on another device, sign in from the first browser before the guest session expires to keep its work.",
        "An account is for one person. Keep your password to yourself. If you think someone else has used your account, write to support@pushbackandchaos.com.",
        "If you forget your password, ask for a link on the sign-in page. If your account has a password or has no way to sign in, we email a link that works once and lasts one hour. Saving a new password confirms your email address and signs you out everywhere. Then sign in with your new password. If you only sign in with Google, use Google to sign in; there's no password here to reset.",
      ],
    },
    {
      heading: "What the service is, and isn't.",
      body: "Pushback & Chaos is for practice. You play a consultant on business cases, and AJ, an AI coach, helps you see how you work and what to try next. The companies, people, documents and figures in the cases are invented. Any resemblance to a real business or person is a coincidence. What happens in a case depends on your choices, on how the case is written, and partly on chance.",
      more: [
        "We don't promise any recruiting, interview, job or academic outcome. Pushback & Chaos is not affiliated with, endorsed by, or sponsored by any consulting firm, employer, school or club.",
        "Your feedback reports, the coach's readings of your skills, and its suggestions are automated estimates meant to help you practice. They are not grades, credentials or certificates, and we don't send them to any employer or school.",
        "The service is free, and nothing is sold here today. We may change it. We may add or remove cases or features, or limit how much one account or session can do. When you reach a limit, the service tells you, and nothing you wrote is lost.",
      ],
    },
    {
      heading: "The AI here.",
      body: "AJ is an AI. So are the Narrator who runs each case and the people you meet in the cases: AI models write their words as you play. Other AI models read your work to write your feedback. The Privacy page names each AI provider and what each one receives.",
      more: [
        "AI can be wrong. It can misread what you meant, contradict itself, or sound sure about something that isn't true. In a case, the documents carry the case's facts, and some arrive incomplete or out of date on purpose. Some people in a case may also mislead you on purpose. That's part of the practice.",
        "Nothing in the service is professional, career, legal, financial, medical or other advice. Don't rely on it for a real decision. If you need advice, ask a qualified person.",
      ],
    },
    {
      heading: "What you write.",
      body: "What you write here is yours: your moves, your recommendations, your reflections, and what you tell the coach. You give us permission to do four things with it, only to run the service for you: store it, process it, show it back to you, and send it to the processors named on the Privacy page. That permission ends when your content is deleted, as the Privacy page describes.",
      more: [
        // The Z.ai qualifier (the review, 2026-10-29; Codex, round two): the terms page is read without
        // the privacy page beside it. TERMS_UPDATED moves at the deploy that ships it, and with it
        // TERMS_VERSION (Andre's ruling 3 of 2026-10-29): every account is asked to accept again.
        "We never sell what you write or use it for advertising. Neither we nor our providers use it to train AI. For Z.ai, that statement rests on its published terms alone, which we cannot verify. We use your session content for our own research or product development only if you separately and explicitly opt in. The default is no.",
        'In settings, you can download a copy of your account. You can take a copy of what the coach keeps about you, or remove what it keeps. You can also delete your account at any time (see "When it ends.").',
        "Please don't paste in other people's personal information, or anything confidential that isn't yours to share, such as a real employer's or client's data. The cases are invented and never need real data. You're responsible for what you write.",
      ],
    },
    {
      heading: "Using it fairly.",
      body: "Don't copy, scrape, download or extract the cases, documents, artwork or other content, except through the downloads the service itself offers. Some of the artwork is licensed to us on terms that forbid anyone extracting it.",
      more: [
        "Don't reverse engineer the service. Don't try to reach its code, its instructions to the AI, or anyone else's data, except where the law gives you that right despite this term.",
        "Don't use bots, scripts or other automated tools to play, create accounts or send requests. Don't try to get around the service's limits, for example by making extra accounts or guest sessions.",
        "Don't attack, overload or probe the service's security, or get in the way of anyone else's use. If you find a security problem, please tell us at support@pushbackandchaos.com.",
        "Don't use the service for anything unlawful, harassing or hateful, or in a way that infringes anyone's rights. Don't share, sell or transfer your account.",
      ],
    },
    {
      heading: "Our work, and your license to use it.",
      body: "Everything in the service that isn't yours belongs to us or to those who license it to us. That includes the cases, documents, characters, artwork, words, design and software, and the Pushback & Chaos name and logo. While you follow these terms, we give you a personal, non-commercial, non-transferable license to use the service for your own practice. It ends when your access ends.",
      more: [
        "Some copies are for your own personal use: the copies you download from settings, and any copies of your coaching record and completed case books we email you at your request. Your own words in them are yours to use as you like. Don't publish, share or sell the case content around them.",
      ],
    },
    {
      heading: "Ideas you send us.",
      body: "If you send us ideas, suggestions or other feedback about the service, we may use them to improve it without owing you anything. We won't name you publicly without asking first.",
    },
    {
      heading: "Email.",
      body: "These account emails are sent regardless of your email choices:",
      list: [
        "a confirmation link when you sign up with an email and password, ask for another link, or sign in with your password before confirming your address;",
        "a password link when you ask to reset your password, or sign up with an address that already has an unconfirmed account that uses only a password;",
        "emails about deleting or keeping your account.",
      ],
      more: [
        "Verification and reset emails come from Pushback & Chaos <support@pushbackandchaos.com>. Everything else, such as news, reminders, invitations or a weekly note from AJ, comes only if you choose it, and you can stop it at any time. The Privacy page's section on email has the details.",
      ],
    },
    {
      heading: "When it ends.",
      // HELD: the backups sentence, as on the privacy page, waits for the release after 2026-11-05
      // (Andre). When it ships, "age out within 14 days of the deletion." becomes "age out within
      // about 21 days of the deletion, because our hosting provider keeps its backups of our server
      // for 7 days." (D8 overridden, 2026-10-28), TERMS_UPDATED moves at that deploy, and whether
      // TERMS_VERSION moves too (a change to people's rights) is Andre's call.
      body: 'You can stop using the service at any time. To delete your account, choose "delete my account." in settings while signed in. Confirming signs you out on every device and schedules deletion 30 days after you confirm. Before then, you can sign back in and choose "keep my account." Deletion runs hourly, so it can take up to one more hour. Backups that still hold your account age out within 14 days of the deletion. A guest session and its work are deleted 30 days after it was made, unless you sign in from its browser first.',
      more: [
        "We may suspend or end your access, as a guest or with an account, if you break these terms. We may also do so when we must, to protect the service or other people, or to follow the law. If you think we've made a mistake, write to support@pushbackandchaos.com.",
        "If we decide to stop offering the service, we'll say so in the product before it closes, so you can download a copy of your account.",
        'Some parts of these terms still apply after they end: ownership, limits on downloads, ideas you sent us, and the sections titled "No other promises.", "Limits on what we owe." and "Which law applies, and where." Site-wide totals hold no account or guest identifier, so they stay after a deletion. Copies the AI providers or Mailjet already hold under their own terms are outside our reach, as the Privacy page says.',
      ],
    },
    {
      heading: "No other promises.",
      body: 'We work to keep the service running and its feedback useful. But it is free, and we provide it "as is" and "as available." To the extent the law allows, we make no promises beyond those written in these terms. That includes any implied warranty of merchantability, fitness for a particular purpose, or non-infringement. We don\'t promise that the service will always be available, free of errors or safe from every attack. We don\'t promise that the AI will be accurate, or that practicing here will lead to any result.',
    },
    {
      heading: "Limits on what we owe.",
      // The draft brackets the figure for counsel ([US$50]); printed here without the brackets.
      body: "To the extent the law allows, we aren't liable for indirect, incidental, special, consequential or punitive losses. We also aren't liable for lost profits, opportunities, data or goodwill that come from using the service or not being able to use it. To the extent the law allows, our total liability for all claims about the service is limited to US$50.",
      more: [
        "Nothing in these terms limits liability that the law doesn't allow to be limited, such as liability for fraud, or for death or personal injury caused by negligence. If you live somewhere that gives consumers rights a contract can't take away, nothing in these terms takes them away.",
      ],
    },
    {
      heading: "Which law applies, and where.",
      body: "The laws of the State of Nevada govern these terms and any dispute about the service, without regard to conflict-of-law rules. Disputes will be decided in the state or federal courts located in Clark County, Nevada, and you and we agree to those courts' jurisdiction. You may be a consumer whose local law lets you bring a claim in your own courts, or keeps its protections for you whatever a contract says. If so, these terms don't take that away. Before going to court, please write to support@pushbackandchaos.com so we can try to sort it out.",
    },
    {
      heading: "Changes to these terms.",
      body: "The date at the top shows when these terms last changed. We'll post any new version here, with a new date, before it applies. If a change matters to your rights, we'll ask you to accept the new version in the product. If you don't agree, you can stop using the service and delete your account. Changes don't apply to anything that happened before their date.",
    },
    {
      heading: "The rest.",
      body: "If a court finds part of these terms unenforceable, the rest still applies. If we don't enforce a term right away, we haven't given it up. You can't transfer these terms to anyone else. We may transfer them to someone who takes over the service, and they'll still protect you as written. These terms and the Privacy page are the whole agreement between you and us about the service.",
    },
    {
      heading: "Contact.",
      body: "For questions about these terms or the service, write to support@pushbackandchaos.com.",
    },
  ],
  // The draft's back control: the route the page was reached from, or sign in when reached cold.
  back: "back.",
  // The page's door to privacy, reviewed by Codex, 2026-10-22.
  privacy: "the privacy page.",
} as const;

/**
 * THE PRIVACY PAGE'S OPT-OUT (ADR 0022), verbatim from Codex's draft
 * (docs/cme/CODEX-DRAFT-ANALYTICS-A-2026-10-08.md, section 2). A switch whose
 * ON state means "do not count"; Global Privacy Control and Do Not Track take
 * precedence over it.
 */
export const VISIT_COUNTING = {
  label: "don't count my visits.",
  on: "on — visits aren't counted.",
  off: "off — visits can be counted unless your browser asks us not to.",
  savedOn: "Your visits won't be counted in this browser.",
  savedOff: "Visit counting can resume unless your browser asks us not to.",
  browserOnly: "This choice is remembered only in this browser.",
  browserSignal: "Your browser asks us not to count visits. We honor that here.",
  saveFailed: "This choice couldn't be saved in this browser. Use Global Privacy Control or Do Not Track to ask us not to count visits.",
} as const;

/**
 * The settings sheet's recruiting fields and the guest note (CME stage 1, ADR
 * 0021), verbatim from Codex's voice pass of 2026-10-06. Stage 1's two email
 * boxes are retired for EMAIL_CHOICES below; "news from Pushback & Chaos" is
 * no longer offered.
 */
export const CONSENT = {
  program: "program",
  programBlank: "leave blank",
  programHint: "Optional. Change it or clear it in settings. Nothing in a case depends on it.",
  programOptions: {
    UNDERGRAD: "undergraduate",
    MBA: "MBA",
    OTHER_GRAD: "other graduate program",
    NONE: "not in a program",
    UNSTATED: "rather not say",
  },
  season: "recruiting season",
  seasonExample: "2026–27",
  seasonHint: "The recruiting season you're aiming for. Optional. Change it or clear it in settings. Nothing in a case depends on it.",
  seasonInvalid: "Use a season like 2026–27, or leave this blank.",
  guestNote: "Sign in to choose emails. As a guest, we have no email address for you.",
} as const;

/**
 * EMAIL CHOICES (2026-10-07): the permanent desk card, its guest version and
 * the settings section, verbatim from Codex's draft
 * (docs/cme/CODEX-DRAFT-EMAIL-CHOICES-2026-10-07.md, sections 2 and 5), in
 * the product voice. The privacy page's "Email from us" section describes the
 * same seven choices; change them together. Nothing shows any of this before
 * the learner's first Harborlight report (the server decides).
 */
export const EMAIL_CHOICES = {
  title: "email choices.",
  intro: "Choose any emails you'd like, or none, and change your choices here anytime.",
  // Stop all (2026-10-21): scoped to the emails chosen here, as the privacy page's "Email from us"
  // body now ends (it was "We send only the emails you choose here, to a verified address.", which
  // the deletion emails contradicted). Wording reviewed and kept by Codex, 2026-10-21.
  holdNotice: "The emails you choose here go only to a verified address.",
  // Andre's ruling 17 (2026-10-12): chosen emails go only to a verified address; this line covers any signed-in learner whose address is still unverified.
  // Wording by Codex (2026-10-12).
  // The account release (2026-10-22): verification exists now. A signed-in account still unverified is
  // a session from before it, or a Google account whose address Google had not verified; an
  // unverified sign-in with a password is sent a new link (`sendOnSignIn`). Reviewed by Codex,
  // 2026-10-22; the send can be held back by its limits.
  unverifiedAddress: "Your address isn't confirmed yet, so we can't send the emails you choose here. They go only to a verified address. If you use email and password, log out and sign in again to ask for a confirmation link.",
  purposes: {
    NEW_CASE: { label: "a new case", hint: "One email when a new case is released." },
    MAJOR_UPDATE: { label: "a major update", hint: "One email when a major update ships." },
    COACH_WEEKLY: { label: "a weekly note from AJ", hint: "Your own coaching note, with what you've worked on and what to try next, in the email itself." },
    NEW_FROM_CREATORS: { label: "something new from the creators", hint: "An email when the people who made Pushback & Chaos release something new with a free demo." },
    SEASON_REMINDERS: { label: "recruiting season reminders", hint: "A short note as your recruiting season approaches and another when it opens." },
    RESEARCH_INVITES: { label: "invitations to give feedback", hint: "Invitations to give feedback or join a short interview, with no obligation to take part." },
    OUTCOME_CHECKIN: {
      label: "an email after recruiting season",
      hint: "One short email after your season ends: how far you got, and whether practice here helped. Your answers are only counted in totals of ten people or more.",
    },
  },
  weeklyOptions: { ALL_YEAR: "all year", RECRUITING_SEASON: "only during my recruiting season" },
  weeklyNote: "Your coaching note goes through Mailjet, which stores it in the EU.",
  weeklyTimingNeeded: "Choose when you'd like your weekly note: all year or only during your recruiting season.",
  seasonNeeded: "Set your recruiting season in settings for season-only notes or reminders.",
  researchNote: "Choosing invitations gives us permission to email you. Permission to use your sessions for research is asked separately.",
  functionalNote: "We'll ask separately before first emailing certificates or results.",
  permanenceNote: "This card stays on your desk. You can collapse it and reopen it anytime.",
  save: "save choices.",
  none: "no emails.",
  collapse: "collapse card.",
  reopen: "open email choices.",
  saved: "Your email choices are saved.",
  savedNone: "Saved. You've chosen no update emails.",
  notSaved: "Your email choices weren't saved. Please try again.",
  guestIntro: "Choose any emails you'd like, or none, once you're signed in.",
  signIn: "sign in.",
  settingsHeading: "email choices.",
  settingsIntro: "Choose any emails you'd like, or none; these are the same choices as on your desk.",
} as const;

/**
 * THE JUST-IN-TIME ASKS (Codex's section 3): one question for each kind of
 * functional email, neither answer chosen, put just before that kind would
 * first be sent. Nothing shows them yet: no certificate or results email exists.
 */
export const FUNCTIONAL_EMAIL_ASKS = {
  CERTIFICATES: { question: "Would you like your certificates by email, starting with this one?", yes: "yes, email my certificates.", no: "no certificate emails." },
  RESULTS: { question: "Would you like your feedback reports and results by email, starting with this one?", yes: "yes, email my reports and results.", no: "no report or result emails." },
} as const;

/** The four questions' copy, keyed by the contract's answer vocabulary, so a word Codex wrote for an answer the contract lacks (or the reverse) fails to compile. */
type CheckInQuestions = {
  readonly [Q in OutcomeQuestion]: {
    readonly question: string;
    readonly answers: { readonly [A in (typeof OUTCOME_ANSWERS)[Q][number]]: string };
  };
};

/**
 * THE OUTCOME CHECK-IN FORM (`#/check-in?token=`, CME stage 3b, 2026-10-11):
 * Codex's section 4 verbatim (docs/cme/CODEX-DRAFT-STAGE-3-COPY-2026-10-07.md),
 * the heading, the four questions with their options in Codex's order, the
 * privacy note under the form, the submit label and the thank-you line. The
 * keys are the contract's (OUTCOME_ANSWERS); the words are Codex's. Single
 * choice, nothing selected in advance, no free text.
 * Closed-form and failure copy by Codex, 2026-10-11.
 */
export const CHECK_IN = {
  heading: "After recruiting season.",
  questions: {
    interviewed: { question: "Did you interview this season?", answers: { yes: "yes", no: "no", rather_not_say: "rather not say" } },
    how_far: {
      question: "How far did you get?",
      answers: { no_interviews: "no interviews", first_round: "first round", later_rounds: "later rounds", final_round: "final round", rather_not_say: "rather not say" },
    },
    offer: { question: "Did you get an offer?", answers: { yes: "yes", no: "no", still_waiting: "still waiting", rather_not_say: "rather not say" } },
    helped: {
      question: "Did practice here help?",
      answers: { yes_a_lot: "yes, a lot", yes_a_little: "yes, a little", no: "no", not_sure: "not sure", did_not_interview: "I did not interview", rather_not_say: "rather not say" },
    },
  } satisfies CheckInQuestions,
  privacyNote: "Your answers are kept with your account. They are only counted in totals of ten people or more. No individual answer is shown to anyone, including any club or school.",
  submit: "send answers.",
  thankYou: "Thank you. Your answers are saved.",
  // The server's closed reasons: already answered, totals frozen, or the check-in choice withdrawn.
  closed: {
    ANSWERED: "Your answers for this season are already saved.",
    WINDOW_CLOSED: "This check-in has closed and its totals are final, so a late answer cannot be counted.",
    NO_CONSENT: "This check-in is closed because emails after recruiting season are no longer selected.",
    // Words by Codex (account erasure, 2026-10-21): only choosing to keep the account reopens the form.
    ACCOUNT_CLOSING: 'This check-in is closed because your account is set to be deleted. Sign in and choose "keep my account." to reopen it.',
  },
  // The send was refused, but the refetched state still reads open.
  closedNow: "Your answers couldn't be accepted this time.",
  // No link in the address, or the server cannot verify it.
  badLink: "This check-in link is missing or invalid.",
  // The form request did not reach the server, or the server did not answer.
  notLoaded: "We couldn't load this check-in; please try again.",
  // A failed send does not establish whether the server saved the answers.
  notSaved: "We couldn't confirm whether your answers were saved; please try again.",
} as const;

/**
 * THE UNSUBSCRIBE PAGE (`#/unsubscribe?token=`, CME stage 3b, 2026-10-11): one
 * heading, one sentence, one button that withdraws the one purpose the link
 * names, then a done sentence. No sign-in is needed.
 * Unsubscribe-page copy by Codex, 2026-10-11.
 *
 * Stop all (2026-10-21; CAN-SPAM's opt-out menu must include stopping every
 * commercial email): a second, separate sentence and button below the first
 * withdraw every email choice at once (`POST /u/:token/all`), with their own
 * done and failed sentences. Deletion emails are not an email choice and
 * still go to someone who asks to delete their account.
 */
export const UNSUBSCRIBE = {
  heading: "Stop these emails.",
  // Wording by Codex, 2026-10-21: the one-kind action comes before the stop-all action.
  sentence: "Stop only the kind of email this link came from. Your other email choices will stay as they are.",
  button: "stop these emails.",
  done: "This kind of email is now turned off.",
  // The request did not reach the server, or the server did not answer.
  failed: "We couldn't confirm whether these emails were stopped; please try again.",
  // No link in the address, or the server cannot verify it.
  badLink: "This unsubscribe link is missing or invalid.",
  // Stop-all wording by Codex, 2026-10-21; deletion and keeping an account have separate emails, and since
  // 2026-10-28 the two security emails are named as separate too (docs/CODEX-DRAFT-OWED-LINES-2026-10-27.md, section 12; the email footer says the same).
  // Every email choice is withdrawn, the functional ones (certificates, results) among them.
  allSentence: "Or turn off every email choice at once. Emails about confirming your address or resetting your password are separate from these choices. Emails about deleting or keeping your account are separate too, and come only when you ask.",
  allButton: "turn off all email choices.",
  allDone: "All your email choices are now turned off.",
  // The request did not reach the server, or the server did not answer.
  allFailed: "We couldn't confirm whether all your email choices were turned off. Please try again.",
} as const;

/**
 * THE VERIFY PAGE (`#/verify?token=`, 2026-10-22, hard verification): the
 * page the verification email's link opens. One heading, one sentence, one
 * button; nothing happens on arrival, so a mail scanner that opens the link
 * confirms nothing and signs nobody in. A link that has expired, was used,
 * or is not one of ours offers a new one. Claude's placeholders for Codex's
 * voice pass; the email's own words are packages/mail's ACCOUNT_EMAIL_WORDS
 * ("The link opens a page with one button. Press it to confirm your address
 * and sign in.").
 */
export const VERIFY_EMAIL = {
  // Words reviewed by Codex, 2026-10-22.
  heading: "Confirm your email address.",
  sentence: "The button confirms your address and signs you in on this browser.",
  // The review's fix (2026-10-22): the address the button confirms, read from the link's token in
  // the browser, so a link someone else sent cannot sign this browser in to their account unseen.
  forAddress: (email: string): string => `Email address to confirm: ${email}.`,
  onlyIfYours: "Only confirm if this is your address.",
  notYou: "If you didn't sign up, close this page and ignore the email. Don't press the button.",
  button: "confirm my address.",
  // The guest claim runs on the first signed-in request from the browser holding the guest cookie.
  guest: "Your guest work comes with you when you sign in here, before the guest session expires, 30 days after it began.",
  // better-auth answers an address already verified the same way and signs nobody in, so the page
  // asks who this is: nobody signed in means the address was confirmed before.
  already: "Your address is already confirmed. You can sign in.",
  signIn: "sign in.",
  // 401 INVALID_TOKEN, TOKEN_EXPIRED or USER_NOT_FOUND: the link cannot sign anyone in again.
  expired: "This link no longer works. It may have expired or been used already.",
  // No token in the address: it may have been removed after use, omitted, or lost by a mail app.
  // The page cannot tell which; offer the email link or a new one.
  badLink: "Open the link from your email in the browser you want to use, or ask for a new one.",
  resendIntro: "Enter your email address to ask for a new confirmation link. It lasts 24 hours.",
  resendButton: "send a new link.",
  resent: "If this address has an unconfirmed account, a new confirmation link is on its way. If it doesn't arrive, wait before asking again: sends are limited to one a minute and five a day.",
  // The request did not reach the server, or the server did not answer.
  notThrough: "We couldn't confirm your address or send your request. Try again in a moment.",
} as const;

/**
 * THE RESET PAGE (`#/reset?token=`, 2026-10-22): the page the password reset
 * email's link opens. A new password twice, at least 12 characters (better-
 * auth's minPasswordLength), then one request. Saving it signs the account
 * out everywhere (revokeSessionsOnPasswordReset, and the claimed guest
 * cookies expired with it), so the page ends on "sign in.". Words reviewed
 * by Codex, 2026-10-22.
 */
export const RESET_PASSWORD = {
  // Words reviewed by Codex, 2026-10-22.
  heading: "Choose a new password.",
  sentence: "Use at least 12 characters and type your new password twice. Saving it also confirms your email address and signs you out everywhere. Then sign in with your new password.",
  newLabel: "new password",
  againLabel: "repeat new password",
  save: "save my new password.",
  mismatch: "The two passwords don't match.",
  done: "Your new password is saved and your email address is confirmed. You're signed out everywhere. Sign in with your new password.",
  signIn: "sign in.",
  // 400 INVALID_TOKEN (expired, used, or not one of ours) or USER_NOT_FOUND.
  expired: "This link no longer works. It may have expired or been used already.",
  // No token in the address; its absence does not tell us whether the link was used or damaged.
  badLink: "Open the link from your email in the browser you want to use, or ask for a new one.",
  askAgain: "ask for a new link.",
  notThrough: "We couldn't save your password. Try again in a moment.",
} as const;

/**
 * THE TERMS PAPER (2026-10-22): laid on the desk at home, never over play,
 * when GET /me/identity says a signed-in account has no acceptance of the
 * current TERMS_VERSION on record (every account that predates the terms,
 * and every account once the version moves). "accept the terms." records it
 * (POST /me/terms, the server's version stamped); "not now." puts the paper
 * down until the app is next opened; nothing is recorded but an acceptance.
 * Words reviewed by Codex, 2026-10-22.
 */
export const TERMS_PAPER = {
  // Words reviewed by Codex, 2026-10-22.
  kind: "terms",
  title: "Our terms of use.",
  body: "We have no acceptance of the current terms on record for your account. Please read them and accept if you agree.",
  read: "read the terms.",
  accept: "accept the terms.",
  later: "not now.",
  notSaved: "We couldn't save your acceptance. Try again in a moment.",
} as const;

/**
 * The settings sheet's quiet row by "done." (2026-10-22): the two pages,
 * each opened in a new tab so a sheet opened over play never leaves it.
 * Codex kept both words on 2026-10-28 (docs/CODEX-DRAFT-OWED-LINES-2026-10-27.md, section 12).
 */
export const LEGAL_LINKS = {
  privacy: "privacy.",
  terms: "terms.",
} as const;

/**
 * THE PRACTICE PANEL (coach plan steps 6 to 8): an exercise beside the
 * conversation, in the coach's lowercase voice. No numeral anywhere: the
 * stage is a word, a criterion's status is a mark and a word.
 */
export const PRACTICE = {
  label: "Practice",
  none: "A small exercise between engagements, chosen for what your record says or for a skill you name.",
  offerOne: "offer me one.",
  choose: "let me choose.",
  chooseCancel: "never mind.",
  accept: "I'll try it.",
  decline: "not this one.",
  task: "Your task:",
  hint: "a hint.",
  example: "show one way it could go.",
  revision: "show my attempt revised.",
  variation: "a different situation.",
  pause: "pause.",
  resume: "pick it back up.",
  concludeDone: "done with this one.",
  concludeEarly: "stop here.",
  hintLabel: "Hint:",
  exampleLabel: "One way it could go:",
  revisionLabel: "Your attempt, revised once. The change is the coach's, not your words.",
  exampleInstead: "The coach could not revise your attempt this time; here is the example instead. Your attempt is kept as you wrote it.",
  attemptPlaceholder: "Write your attempt here.",
  revisePlaceholder: "Try it again, in your own words.",
  reading: "Reading your attempt.",
  /** A desk action that never reached the server; a refusal that did is said in the server's words. */
  unreachable: "That did not go through. Try it again in a moment.",
  markPresent: "there:",
  markMissing: "not yet:",
  markUnread: "to write:",
  stageFinished: "every part is on the page",
  stages: { OFFERED: "offered", ACCEPTED: "yours to try", ATTEMPTED: "in progress", PAUSED: "paused", CONCLUDED: "concluded", DECLINED: "declined" },
  /** THE CARD ON THE DESK (2026-09-23): the exercise as a paper in the case card's design. */
  card: {
    kind: "practice",
    noneTitle: "A small exercise.",
    noneByline: "Between engagements, for what your record says or for a skill you name.",
    doorNone: "offer me one.",
    doorOffered: "I'll try it.",
    doorOpen: "open.",
    rowSkill: "skill:",
    rowStage: "stage:",
    stageNone: "none in progress",
  },
} as const;

/**
 * THE FOCUS (coach plan step 9, C08): one thing the learner is working on,
 * shown beside the exercise. Every line about what stands behind it is one
 * of the plan's five kinds of evidence, in its own words: chosen, tried,
 * said, shown, across situations. Nothing here is a numeral and nothing
 * upgrades one kind into another.
 */
export const FOCUS = {
  label: "Focus",
  none: "One thing to work on between engagements: the coach can suggest it from your record, or you can name it.",
  suggest: "suggest one.",
  name: "let me name it.",
  namePlaceholder: "What do you want to work on, in your own words? Name the skill.",
  nameCancel: "never mind.",
  choose: "make it my focus.",
  decline: "not this.",
  done: "done with this.",
  report: "I used it elsewhere.",
  reportPlaceholder: "Where did you use it, and how did it go? Your words, kept as your words.",
  reportWhere: "Where was that?",
  reportContexts: { WORK: "at work", ENGAGEMENT: "in an engagement", ELSEWHERE: "somewhere else" },
  reportCancel: "never mind.",
  saidLabel: "You said:",
  reviewLabel: "What a later engagement showed:",
  noReviewYet: "No engagement finished since you chose this has been looked at against it yet.",
  provenance: {
    LEARNER_CHOSEN: "You named it.",
    PLAN: "Suggested from your record.",
    COACH_SUGGESTED: "The coach suggested it.",
    LEGACY_NOTES: "Suggested from an earlier coach note.",
  },
  standing: {
    chosen: "you chose it",
    tried: "you tried it in an exercise",
    selfReported: "you said you used it elsewhere",
    shownInEngagement: "a finished engagement showed it",
    acrossSituations: "it has shown up across situations",
  },
  verdicts: { NO_OPPORTUNITY: "no moment for it", SUPPORTED: "it showed", MIXED: "it read both ways", CONTRADICTED: "it did not show" },
  statuses: { SUGGESTED: "suggested", CHOSEN: "yours", DECLINED: "declined", REPLACED: "replaced", DONE: "done" },
  working: "Keeping that.",
  /** THE CARD ON THE DESK (2026-09-23): the focus as a paper in the case card's design. */
  card: {
    kind: "focus",
    noneTitle: "One thing to work on.",
    noneByline: "Suggested from your record, or named by you.",
    doorNone: "suggest one.",
    /** Before any engagement is finished there is no record to suggest from: the door opens the naming box instead. */
    doorName: "name one.",
    doorSuggested: "make it my focus.",
    doorChosen: "open.",
    rowSkill: "skill:",
    rowStanding: "standing:",
    standingNone: "not chosen yet",
  },
} as const;

/**
 * WHAT THE COACH KEEPS, AND ITS REMOVAL (Law 5, coach plan step 9): the two
 * controls the privacy page promises, on the settings sheet.
 */
export const ACCOUNT_DATA = {
  heading: "your account, to take with you",
  body: "Take a copy of your profile, engagements, feedback, coach conversations, saved cases, settings and consent history. Your email record and recruiting answers come with it too. Unfinished cases keep their hidden answers; the file explains what is held back and why.",
  export: "download my whole account.",
  exportFile: "pushback-and-chaos-account.json",
} as const;

export const COACH_DATA = {
  heading: "what your coach keeps",
  body: "Your talks with AJ, AJ's notes and greeting, the cases AJ suggested, your practice exercises, your focus and how you met. You can take a copy or remove all of this. Your profile stays, along with your engagements and their results.",
  export: "give me a copy.",
  exportFile: "pushback-and-chaos-coach-data.json",
  remove: "remove it all.",
  confirm: "yes, remove it.",
  /**
   * What goes and what stays, exactly (2026-10-11). The profile row stays
   * (results, baselines and progression hang off it), so the coach does NOT
   * meet the learner again as a stranger; the earlier sentence said it would.
   */
  confirmBody: "This permanently removes your talks with AJ, AJ's notes and greeting, the cases AJ suggested, your practice exercises, your focus and how you met. Your profile stays, along with your engagements and their results.",
  cancel: "keep it.",
  removed: "Removed. The coach starts again from your record.",
} as const;

/**
 * SELF-SERVE ACCOUNT ERASURE (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md, Andre's decisions of
 * 2026-10-20). Words reviewed and revised by Codex, 2026-10-21.
 * Every moment is written like "November 19, 2026, at 5:30 PM",
 * on the learner's own clock (`erasureMoment`, ui/account-erasure.tsx): the same instant the
 * confirmation email states in UTC. No screen says an email was sent: the screen knows only
 * that mail was queued, so the most it says is "on its way".
 */
export const ERASURE = {
  // The settings door, below "download my whole account.", for an account only.
  heading: "deleting your account",
  body: 'Your account is deleted 30 days after you confirm. Until then, you can sign back in and choose "keep my account."',
  door: "delete my account.",

  // The dialog the door opens. The explanation is the design's list, one line each.
  kind: "your account",
  title: "Delete your account?",
  explanation: [
    "Everything in your account goes, including work saved as a guest before you signed in: your engagements and their books, your results, what your coach keeps, your profile and your settings.",
    'Confirming signs you out on every device. Your account is deleted 30 days after you confirm. Until then, you can sign back in and choose "keep my account."',
    // HELD: the privacy page's backups sentence, as live, until the release after 2026-11-05
    // (Andre). Then: "Backups that still hold your account age out within about 21 days of the
    // deletion, because our hosting provider keeps its backups of our server for 7 days." (D8
    // overridden, 2026-10-28: the hosting provider's server backups, kept 7 days.)
    "Backups that still hold your account age out within 14 days of the deletion.",
    "Copies the AI providers or Mailjet already hold under their own terms are outside our reach.",
    "Site-wide totals hold no account identifier and are not reduced.",
  ],
  /** A verified address: two empty boxes. The chosen copies come with the confirmation email. */
  copiesByEmail: "Choose copies to receive with your confirmation email.",
  copyCoaching: "email me a copy of my coaching record",
  copyCasebooks: "email me my completed case books",
  /** An unverified address (ruling 17: it may be someone else's): no boxes, the two downloads instead. */
  copiesUnverified: "Your email address isn't verified, so we don't email copies to it. Download them now instead.",
  /** The address's state not read yet, or not readable: the two downloads, saying nothing about the address. */
  copiesUnread: "Download copies now if you want them.",
  downloadCoaching: "download my coaching record.",
  downloadAccount: "download my whole account.",
  downloadAccountHint: "Your completed case books are in this file, with the rest of your account.",
  /** Either download, failed: said inside the dialog, since the app's own notice is behind it. */
  downloadFailed: "That download didn't go through. Try again in a moment.",
  confirm: "delete my account.",
  cancel: "keep my account.",
  working: "Requesting account deletion.",
  failed: "Your deletion request didn't go through. Nothing has changed. Try again in a moment.",
  /** The answer was lost and asking who this is did not settle it: claims neither outcome. */
  failedUnsure: "We couldn't confirm whether your deletion request went through. Reload the page and sign in if asked to find out.",

  // The notice after confirming, over the landing page, once this browser has logged out.
  requestedTitle: (moment: string): string => `Your account will be deleted on ${moment}.`,
  /** The answer was lost, and the sign-ins' end says it went through, but nothing holds the date: it was asked for just now. */
  requestedTitleUndated: "Your account will be deleted 30 days from today.",
  requestedSignedOut: "You're signed out on every device.",
  /** The confirmation waits in the outbox and the address was verified: the chosen copies go with it. */
  emailWithCopies: "A confirmation email is on its way, with any copies you chose.",
  /** It waits, and the address was not verified: no copies go with it. */
  emailNoCopies: "A confirmation email is on its way. Copies can only be emailed to a verified address.",
  /** Nothing waits for this request (a repeat after the confirmation went out). */
  emailNone: "No new email is on its way for this request.",
  /** The answer was lost: whether a confirmation was queued is not known, so no promise either way. */
  emailUnknown: "We couldn't confirm whether an email is on its way to your address.",
  requestedKeep: 'Until then, you can sign back in and choose "keep my account."',
  done: "done.",

  // The full-screen notice a pending account sees instead of the app.
  graceTitle: (moment: string): string => `Your account will be deleted on ${moment}.`,
  graceBody: 'Choose "keep my account." to cancel the deletion and return to the app. Choosing "log out." leaves the deletion scheduled.',
  keep: "keep my account.",
  logOut: "log out.",
  keepFailed: "We couldn't confirm that your account was kept. Try again in a moment.",
  logOutFailed: "We couldn't confirm that you're logged out. Try again in a moment.",
} as const;

/**
 * THE CASEBOOK (2026-09-24, Andre; with Codex's notes): a finished engagement
 * read back as a book. The play screen's own vocabulary, closed: no composer,
 * a quiet footer, and the closing pages in the debrief's words.
 */
export const CASEBOOK = {
  /** The running head's right side (Codex: short, not the date). */
  runningHead: "closed engagement",
  closedLine: "This engagement is closed. The book keeps it as it happened.",
  contents: "contents.",
  tape: "the tape.",
  /** Not "open the case again.": a retry begins another engagement and keeps this one. */
  tryAgain: "try the case again.",
  theEnd: "the end.",
  backToPage: "back to the page.",
  contentsEyebrow: "The casebook",
  contentsTitle: "Contents",
  titlePage: "Title page",
  prologue: "Prologue",
  readingHere: "you were reading here",
  recommendationTitle: "Your recommendation.",
  recommendationLead: "Your recommendation, as submitted.",
  recommendationKind: "recommendation · your words",
  examTitle: "In the room.",
  afterEyebrow: "After the engagement",
  afterContents: "After the engagement.",
  truth: "What was actually true.",
  turned: "Where it turned.",
  wouldHaveFound: "What you would have found.",
  read: "How the work was read.",
  coach: "Your coach, on this engagement.",
  coachAdded: (date: string): string => `Coach's note, added ${date}.`,
  coachMissing: "Your coach has not written this note yet. It will be here next time you open the book.",
  endings: {
    presented: "presented",
    presented_at_deadline: "presented at the deadline",
    time_expired: "the deadline came first",
  },
  loading: "Opening the book.",
  failed: "This book could not be opened just now. It is still there; try again in a moment.",
  /** The library's box and its keepsakes. */
  completedBox: "Completed",
  completedNote: "read them again",
  completedEmpty: "Finished engagements are kept here, each as a book.",
  keepsakeKind: "casebook",
  readTheBook: "read the book.",
} as const;

/**
 * THE ARRIVAL BOOK (2026-10-11, Andre): how a learner and AJ met, read back as
 * the record's first book. The casebook's vocabulary, closed: a title page
 * with the learner's own name where a case shows its industry, four chapters,
 * and one door back to the record.
 */
// Words by Codex, 2026-10-11.
export const ARRIVAL = {
  /** The record's row, above the engagements. */
  rowTitle: "When you met AJ",
  readTheBook: "read how we met.",
  /** The title page's name for the book. */
  title: "How we met",
  titleEyebrow: "Your first book",
  /** The running head's right side, where a casebook says "closed engagement". */
  runningHead: "our first meeting",
  closedLine: "This is where we began. Your words and mine stay as they were.",
  contentsEyebrow: "Our first meeting",
  chapters: {
    met: "Our first conversation.",
    readBack: "What I read back.",
    rehearsal: "Your rehearsal move.",
    papers: "What I put on your desk.",
  },
  speaker: "AJ",
  /** Over each quote AJ kept: what the learner's words settled. Keyed by the intake's field names. */
  evidenceLabel: {
    name: "what you go by",
    experience_level: "your experience so far",
    target_functions: "the work you're aiming for",
    target_firms: "where you'd like to work",
    self_identified_gaps: "what you want to get better at",
    approaches: "how you tend to approach the work",
    focus_skill: "where you want to start",
  } as Readonly<Record<string, string>>,
  evidenceOther: "what you said",
  focusLabel: "Where you wanted to begin",
  rehearsalSkipped: "You skipped the rehearsal, so there's no move to read here.",
  rehearsalNotKept: "You made your rehearsal move before we started keeping them, so it isn't in this book.",
  papersPlaced: (date: string): string => `I put these on your desk on ${date}.`,
  loading: "Getting your book.",
  failed: "I couldn't open your book just now. Try again in a moment.",
  // Wording adjusted by Claude for accuracy, 2026-10-11; Codex to review. The book
  // goes with the coach's data (settings, "remove it all."), and the old line
  // promised one that could never begin again.
  none: "There's no book here. It begins when you confirm what I read back, and it goes if you remove what I keep.",
  back: "return to your record.",
} as const;
